import { useState, useEffect, useRef, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t } from '../i18n';

const API_BASE = '/api/games';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const colors = {
  bg: '#F7F5F0', surface: '#fff', border: '#E3E1D9', text: '#1F1E1B',
  textMuted: '#5F5E5A', primary: '#0F6E56', primaryBg: '#E1F5EE', danger: '#A32D2D',
};

async function submitScore(game, score) {
  try {
    const res = await fetch(`${API_BASE}/submit/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game, score }) });
    if (res.ok) return await safeJson(res);
  } catch (e) {}
  return null;
}

async function submitStreakResult(game, won) {
  try {
    const res = await fetch(`${API_BASE}/submit-streak/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game, won }) });
    if (res.ok) return await safeJson(res);
  } catch (e) {}
  return null;
}

async function fetchStreak(game) {
  try {
    const res = await fetch(`${API_BASE}/streak/${game}/`, { headers: authHeaders() });
    if (res.ok) return await safeJson(res);
  } catch (e) {}
  return null;
}

function GamesHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 16 };
  const headingStyle = { fontSize: 15, fontWeight: 600, margin: '0 0 6px' };
  const textStyle = { fontSize: 13, color: '#3A3935', lineHeight: 1.5, margin: '0 0 4px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 19 }}>{t('gh_how_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_snake')}</p><p style={textStyle}>{t('gh_snake_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_tetris')}</p><p style={textStyle}>{t('gh_tetris_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_plat')}</p><p style={textStyle}>{t('gh_plat_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_fighter')}</p><p style={textStyle}>{t('gh_fighter_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_uno_n')}</p><p style={textStyle}>{t('gh_uno_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_memory')}</p><p style={textStyle}>{t('gh_memory_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_penalty')}</p><p style={textStyle}>{t('gh_penalty_h')}</p></div>
        <div style={sectionStyle}><p style={headingStyle}>{t('gh_pred')}</p><p style={textStyle}>{t('gh_pred_h')}</p></div>
        <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 12 }}>{t('gh_pricing')}</p>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>{t('gh_got_it')}</button>
      </div>
    </div>
  );
}

function LeaderboardPanel({ game }) {
  const [board, setBoard] = useState([]);
  const [myBest, setMyBest] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/leaderboard/${game}/`);
      if (res.ok) setBoard((await safeJson(res)).leaderboard);
    } catch (e) {}
    if (getAuthToken()) {
      try {
        const res2 = await fetch(`${API_BASE}/my-bests/`, { headers: authHeaders() });
        if (res2.ok) setMyBest((await res2.json())[game]);
      } catch (e) {}
    }
  }, [game]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ marginTop: 16 }}>
      {myBest !== null && <p style={{ fontSize: 13, color: colors.primary, marginBottom: 8 }}>{t('gh_your_best')}<b>{myBest}</b></p>}
      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('gh_lead')}</p>
      {board.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('gh_no_scores')}</p> : (
        board.slice(0, 10).map((b, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: `0.5px solid ${colors.border}` }}>
            <span>{i + 1}. @{b.username}</span>
            <span style={{ fontWeight: 600 }}>{b.score}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ==================== SNAKE ====================

function SnakeGame() {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState('medium');
  const stateRef = useRef(null);

  const GRID = 15;
  const CELLS = 20;
  const SIZE = GRID * CELLS;
  const SPEED_MS = { slow: 160, medium: 110, fast: 70 };

  const startGame = () => {
    stateRef.current = {
      snake: [{ x: 10, y: 10 }, { x: 9, y: 10 }, { x: 8, y: 10 }],
      dir: { x: 1, y: 0 },
      nextDir: { x: 1, y: 0 },
      food: { x: 5, y: 5 },
    };
    setScore(0);
    setGameOver(false);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const placeFood = () => {
      const s = stateRef.current;
      let pos;
      do {
        pos = { x: Math.floor(Math.random() * CELLS), y: Math.floor(Math.random() * CELLS) };
      } while (s.snake.some((seg) => seg.x === pos.x && seg.y === pos.y));
      s.food = pos;
    };

    const onKey = (e) => {
      const s = stateRef.current;
      if (!s) return;
      if (e.key === 'ArrowUp' && s.dir.y === 0) s.nextDir = { x: 0, y: -1 };
      if (e.key === 'ArrowDown' && s.dir.y === 0) s.nextDir = { x: 0, y: 1 };
      if (e.key === 'ArrowLeft' && s.dir.x === 0) s.nextDir = { x: -1, y: 0 };
      if (e.key === 'ArrowRight' && s.dir.x === 0) s.nextDir = { x: 1, y: 0 };
    };
    window.addEventListener('keydown', onKey);

    const tick = () => {
      const s = stateRef.current;
      if (!s) return;
      s.dir = s.nextDir;
      const head = { x: s.snake[0].x + s.dir.x, y: s.snake[0].y + s.dir.y };

      if (head.x < 0 || head.x >= CELLS || head.y < 0 || head.y >= CELLS || s.snake.some((seg) => seg.x === head.x && seg.y === head.y)) {
        setRunning(false);
        setGameOver(true);
        submitScore('snake', score);
        return;
      }

      s.snake.unshift(head);
      if (head.x === s.food.x && head.y === s.food.y) {
        setScore((sc) => sc + 10);
        placeFood();
      } else {
        s.snake.pop();
      }

      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, SIZE, SIZE);
      ctx.fillStyle = colors.danger;
      ctx.beginPath();
      ctx.arc(s.food.x * GRID + GRID / 2, s.food.y * GRID + GRID / 2, GRID / 2 - 2, 0, Math.PI * 2);
      ctx.fill();
      s.snake.forEach((seg, i) => {
        if (i === 0) {
          // Head: bigger, brighter, with two small eyes so it's unmistakable
          ctx.fillStyle = '#4ade80';
          ctx.beginPath();
          ctx.roundRect(seg.x * GRID - 1, seg.y * GRID - 1, GRID + 1, GRID + 1, 5);
          ctx.fill();
          ctx.fillStyle = '#0a0a0a';
          const eyeSize = 2;
          const cx = seg.x * GRID + GRID / 2, cy = seg.y * GRID + GRID / 2;
          const ex1 = cx + s.dir.x * 3 - s.dir.y * 3, ey1 = cy + s.dir.y * 3 - s.dir.x * 3;
          const ex2 = cx + s.dir.x * 3 + s.dir.y * 3, ey2 = cy + s.dir.y * 3 + s.dir.x * 3;
          ctx.beginPath(); ctx.arc(ex1, ey1, eyeSize, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(ex2, ey2, eyeSize, 0, Math.PI * 2); ctx.fill();
        } else {
          ctx.fillStyle = '#16a34a';
          ctx.beginPath();
          ctx.roundRect(seg.x * GRID + 1, seg.y * GRID + 1, GRID - 2, GRID - 2, 3);
          ctx.fill();
        }
      });
    };

    const interval = setInterval(tick, SPEED_MS[speed]);
    return () => { clearInterval(interval); window.removeEventListener('keydown', onKey); };
  }, [running, score, speed]);

  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Score: {score}</p>
      <canvas ref={canvasRef} width={SIZE} height={SIZE} style={{ border: `2px solid ${colors.border}`, borderRadius: 8, maxWidth: '100%', touchAction: 'none' }} />
      {!running && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10 }}>
          {['slow', 'medium', 'fast'].map((s) => (
            <button key={s} onClick={() => setSpeed(s)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', border: `1px solid ${colors.border}`, borderRadius: 16, background: speed === s ? colors.primary : '#fff', color: speed === s ? '#fff' : colors.textMuted, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        {!running && (
          <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
            {gameOver ? t('gh_play_again') : t('gh_start')}
          </button>
        )}
      </div>
      {gameOver && <p style={{ color: colors.danger, marginTop: 8 }}>Game over! Final score: {score}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 50px)', gap: 4, margin: '12px auto 0', width: 160 }}>
        <div />
        <button onTouchStart={() => stateRef.current && stateRef.current.dir.y === 0 && (stateRef.current.nextDir = { x: 0, y: -1 })} style={{ padding: 10 }}>⬆️</button>
        <div />
        <button onTouchStart={() => stateRef.current && stateRef.current.dir.x === 0 && (stateRef.current.nextDir = { x: -1, y: 0 })} style={{ padding: 10 }}>⬅️</button>
        <button onTouchStart={() => stateRef.current && stateRef.current.dir.y === 0 && (stateRef.current.nextDir = { x: 0, y: 1 })} style={{ padding: 10 }}>⬇️</button>
        <button onTouchStart={() => stateRef.current && stateRef.current.dir.x === 0 && (stateRef.current.nextDir = { x: 1, y: 0 })} style={{ padding: 10 }}>➡️</button>
      </div>
      <LeaderboardPanel game="snake" />
    </div>
  );
}
// ==================== TETRIS ====================

const TETROMINOES = {
  I: [[1, 1, 1, 1]],
  O: [[1, 1], [1, 1]],
  T: [[0, 1, 0], [1, 1, 1]],
  S: [[0, 1, 1], [1, 1, 0]],
  Z: [[1, 1, 0], [0, 1, 1]],
  J: [[1, 0, 0], [1, 1, 1]],
  L: [[0, 0, 1], [1, 1, 1]],
};
const TETRO_COLORS = { I: '#0EA5E9', O: '#F2C879', T: '#3b82f6', S: '#22C55E', Z: '#EF4444', J: '#3B82F6', L: '#F97316' };
const TETRO_KEYS = Object.keys(TETROMINOES);

function rotateMatrix(m) {
  const rows = m.length, cols = m[0].length;
  const result = Array.from({ length: cols }, () => Array(rows).fill(0));
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) result[c][rows - 1 - r] = m[r][c];
  return result;
}

function mulberry32(seed) {
  let s = seed;
  return function () {
    s |= 0; s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const GAME_API_BASE = '/api/games';
const WS_BASE = `wss://${window.location.host}`;

function TetrisEngine({ seeded, rngRef, onLinesChange, onToppedOut, opponentPanel, dropInterval = 500 }) {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [running, setRunning] = useState(true);
  const stateRef = useRef(null);

  const COLS = 10, ROWS = 18, CELL = 16;

  const nextRandom = () => (seeded && rngRef.current ? rngRef.current() : Math.random());

  const spawnPiece = () => {
    const key = TETRO_KEYS[Math.floor(nextRandom() * TETRO_KEYS.length)];
    return { key, shape: TETROMINOES[key], x: Math.floor(COLS / 2) - 1, y: 0 };
  };

  const collides = (grid, piece, ox = 0, oy = 0) => {
    for (let r = 0; r < piece.shape.length; r++) {
      for (let c = 0; c < piece.shape[r].length; c++) {
        if (!piece.shape[r][c]) continue;
        const x = piece.x + c + ox, y = piece.y + r + oy;
        if (x < 0 || x >= COLS || y >= ROWS) return true;
        if (y >= 0 && grid[y][x]) return true;
      }
    }
    return false;
  };

  useEffect(() => {
    stateRef.current = {
      grid: Array.from({ length: ROWS }, () => Array(COLS).fill(null)),
      current: spawnPiece(),
      linesCleared: 0,
    };
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const onKey = (e) => {
      const s = stateRef.current;
      if (!s || gameOver) return;
      if (e.key === 'ArrowLeft' && !collides(s.grid, s.current, -1, 0)) s.current.x -= 1;
      if (e.key === 'ArrowRight' && !collides(s.grid, s.current, 1, 0)) s.current.x += 1;
      if (e.key === 'ArrowDown' && !collides(s.grid, s.current, 0, 1)) s.current.y += 1;
      if (e.key === 'ArrowUp') {
        const rotated = { ...s.current, shape: rotateMatrix(s.current.shape) };
        if (!collides(s.grid, rotated)) s.current = rotated;
      }
    };
    window.addEventListener('keydown', onKey);

    const lockPiece = (s) => {
      s.current.shape.forEach((row, r) => row.forEach((v, c) => {
        if (v && s.current.y + r >= 0) s.grid[s.current.y + r][s.current.x + c] = s.current.key;
      }));
      let cleared = 0;
      s.grid = s.grid.filter((row) => {
        if (row.every((cell) => cell)) { cleared++; return false; }
        return true;
      });
      while (s.grid.length < ROWS) s.grid.unshift(Array(COLS).fill(null));
      if (cleared) {
        s.linesCleared += cleared;
        setScore((sc) => { const ns = sc + cleared * 100; onLinesChange && onLinesChange(s.linesCleared, ns); return ns; });
      }
      s.current = spawnPiece();
      if (collides(s.grid, s.current)) {
        setRunning(false);
        setGameOver(true);
        onToppedOut && onToppedOut();
      }
    };

    const draw = (s) => {
      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, COLS * CELL, ROWS * CELL);
      s.grid.forEach((row, r) => row.forEach((cell, c) => {
        if (cell) { ctx.fillStyle = TETRO_COLORS[cell]; ctx.fillRect(c * CELL, r * CELL, CELL - 1, CELL - 1); }
      }));
      ctx.fillStyle = TETRO_COLORS[s.current.key];
      s.current.shape.forEach((row, r) => row.forEach((v, c) => {
        if (v) ctx.fillRect((s.current.x + c) * CELL, (s.current.y + r) * CELL, CELL - 1, CELL - 1);
      }));
    };

    const interval = setInterval(() => {
      const s = stateRef.current;
      if (!s || gameOver) return;
      if (!collides(s.grid, s.current, 0, 1)) {
        s.current.y += 1;
      } else {
        lockPiece(s);
      }
      draw(s);
    }, dropInterval);

    draw(stateRef.current);
    return () => { clearInterval(interval); window.removeEventListener('keydown', onKey); };
  }, [gameOver, dropInterval]);

  const touchMove = (dx, dy, rotate) => {
    const s = stateRef.current;
    if (!s || gameOver) return;
    if (rotate) {
      const r = { ...s.current, shape: rotateMatrix(s.current.shape) };
      if (!collides(s.grid, r)) s.current = r;
    } else if (!collides(s.grid, s.current, dx, dy)) {
      s.current.x += dx; s.current.y += dy;
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Score: {score}</p>
          <canvas ref={canvasRef} width={COLS * CELL} height={ROWS * CELL} style={{ border: `2px solid ${colors.border}`, borderRadius: 8, background: colors.bg }} />
        </div>
        {opponentPanel}
      </div>
      {gameOver && <p style={{ color: colors.danger, marginTop: 8 }}>Game over! Final score: {score}</p>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 12 }}>
        <button onTouchStart={() => touchMove(-1, 0)} style={{ padding: '10px 14px' }}>⬅️</button>
        <button onTouchStart={() => touchMove(0, 0, true)} style={{ padding: '10px 14px' }}>🔄</button>
        <button onTouchStart={() => touchMove(1, 0)} style={{ padding: '10px 14px' }}>➡️</button>
        <button onTouchStart={() => touchMove(0, 1)} style={{ padding: '10px 14px' }}>⬇️</button>
      </div>
    </div>
  );
}

function TetrisSinglePlayer() {
  const rngRef = useRef(null);
  const finalScoreRef = useRef(0);
  const [speed, setSpeed] = useState('medium');
  const [started, setStarted] = useState(false);
  const SPEED_MS = { slow: 700, medium: 500, fast: 300 };

  if (!started) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t('gh_speed')}</p>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
          {['slow', 'medium', 'fast'].map((s) => (
            <button key={s} onClick={() => setSpeed(s)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', border: `1px solid ${colors.border}`, borderRadius: 16, background: speed === s ? colors.primary : '#fff', color: speed === s ? '#fff' : colors.textMuted, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>
        <button onClick={() => setStarted(true)} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_start')}</button>
      </div>
    );
  }

  return (
    <TetrisEngine
      seeded={false}
      rngRef={rngRef}
      onLinesChange={(lines, sc) => { finalScoreRef.current = sc; }}
      onToppedOut={() => submitScore('tetris', finalScoreRef.current)}
      dropInterval={SPEED_MS[speed]}
    />
  );
}

function TetrisMultiplayer({ onExit }) {
  const [phase, setPhase] = useState('lobby'); // lobby | waiting | matched | playing | done
  const [opponent, setOpponent] = useState('');
  const [seed, setSeed] = useState(null);
  const [room, setRoom] = useState('');
  const [myLines, setMyLines] = useState(0);
  const [oppLines, setOppLines] = useState(0);
  const [result, setResult] = useState('');
  const wsRef = useRef(null);
  const rngRef = useRef(null);
  const pollRef = useRef(null);
  const finalScoreRef = useRef(0);

  const findMatch = async () => {
    setPhase('waiting');
    const poll = async () => {
      try {
        const res = await fetch(`${GAME_API_BASE}/find-match/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game: 'tetris' }) });
        const data = await safeJson(res);
        if (data.matched) {
          clearInterval(pollRef.current);
          setOpponent(data.opponent);
          setSeed(data.seed);
          setRoom(data.room);
          rngRef.current = mulberry32(data.seed);
          setPhase('matched');
          connectWs(data.room);
          setTimeout(() => setPhase('playing'), 2000);
        }
      } catch (e) {}
    };
    poll();
    pollRef.current = setInterval(poll, 1500);
  };

  const cancelSearch = async () => {
    clearInterval(pollRef.current);
    await fetch(`${GAME_API_BASE}/cancel-match/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game: 'tetris' }) });
    setPhase('lobby');
  };

  const connectWs = (roomName) => {
    const token = getAuthToken();
    const myUsername = localStorage.getItem('username') || '';
    const ws = new WebSocket(`${WS_BASE}/ws/game/${roomName}/?token=${token}`);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.username === myUsername) return; // ignore echo of our own messages
      if (data.type === 'lines_update') setOppLines(data.lines);
      if (data.type === 'topped_out') { setResult('win'); setPhase('done'); submitScore('tetris', finalScoreRef.current); }
      if (data.type === 'player_left' && data.username !== myUsername) {
        setResult('win');
        setPhase((p) => (p === 'playing' ? 'done' : p));
        submitScore('tetris', finalScoreRef.current);
      }
    };
    wsRef.current = ws;
    wsRef.current.myUsername = myUsername;
  };

  useEffect(() => () => { clearInterval(pollRef.current); wsRef.current && wsRef.current.close(); }, []);

  const handleLinesChange = (lines, sc) => {
    setMyLines(lines);
    finalScoreRef.current = sc;
    if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify({ type: 'lines_update', lines, username: wsRef.current.myUsername }));
  };

  const handleToppedOut = () => {
    if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify({ type: 'topped_out', username: wsRef.current.myUsername }));
    setResult('lose');
    setPhase('done');
    submitScore('tetris', finalScoreRef.current);
  };

  if (phase === 'lobby') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 14, color: colors.textMuted, marginBottom: 16 }}>{t('gh_race_h')}</p>
        <button onClick={findMatch} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_find_opp')}</button>
      </div>
    );
  }

  if (phase === 'waiting') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 14, marginBottom: 12 }}>{t('gh_waiting')}</p>
        <button onClick={cancelSearch} style={{ padding: '8px 20px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_cancel')}</button>
      </div>
    );
  }

  if (phase === 'matched') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary }}>Opponent found: @{opponent}!</p>
        <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 8 }}>{t('gh_starting')}</p>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 18, fontWeight: 600, color: result === 'win' ? colors.primary : colors.danger, marginBottom: 12 }}>
          {result === 'win' ? `🎉 You beat @${opponent}!` : `@${opponent} topped out first - you lost this round.`}
        </p>
        <button onClick={() => { setPhase('lobby'); setMyLines(0); setOppLines(0); }} style={{ padding: '10px 24px', fontSize: 14, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_play_again')}</button>
      </div>
    );
  }

  // playing
  return (
    <TetrisEngine
      seeded={true}
      rngRef={rngRef}
      onLinesChange={handleLinesChange}
      onToppedOut={handleToppedOut}
      opponentPanel={
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>@{opponent}</p>
          <div style={{ width: 60, height: 18 * 16, border: `2px solid ${colors.border}`, borderRadius: 8, background: colors.bg, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 4 }}>
            <div style={{ width: '100%', height: `${Math.min(100, oppLines * 8)}%`, background: colors.danger, borderRadius: 4 }} />
          </div>
          <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>{oppLines} lines</p>
        </div>
      }
    />
  );
}

function TetrisGame() {
  const [mode, setMode] = useState(null);

  if (!mode) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{t('gh_tetris')}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={() => setMode('single')} style={{ padding: '10px 20px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_single')}</button>
          <button onClick={() => setMode('multi')} style={{ padding: '10px 20px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: '#1D4ED8', color: '#fff', cursor: 'pointer' }}>{t('gh_multi')}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setMode(null)} style={{ fontSize: 12, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 10 }}>{t('gh_back_mode')}</button>
      {mode === 'single' ? <TetrisSinglePlayer /> : <TetrisMultiplayer />}
      <LeaderboardPanel game="tetris" />
    </div>
  );
}
// ==================== PLATFORMER ====================

function PlatformerGame() {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [won, setWon] = useState(false);
  const [speed, setSpeed] = useState('medium');
  const stateRef = useRef(null);
  const keysRef = useRef({});
  const SPEED_MULT = { slow: 0.6, medium: 1, fast: 1.5 };

  const W = 320, H = 240;

  const buildLevel = () => ({
    player: { x: 20, y: 180, vx: 0, vy: 0, w: 16, h: 20, onGround: false },
    platforms: [
      { x: 0, y: 220, w: 320, h: 20 },
      { x: 60, y: 170, w: 60, h: 10 },
      { x: 160, y: 140, w: 60, h: 10 },
      { x: 240, y: 100, w: 60, h: 10 },
      { x: 40, y: 90, w: 60, h: 10 },
    ],
    coins: [
      { x: 80, y: 150, taken: false }, { x: 180, y: 120, taken: false },
      { x: 260, y: 80, taken: false }, { x: 60, y: 70, taken: false },
    ],
    timeLeft: 30000,
  });

  const startGame = () => {
    stateRef.current = buildLevel();
    setScore(0);
    setGameOver(false);
    setWon(false);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const onKeyDown = (e) => { keysRef.current[e.key] = true; };
    const onKeyUp = (e) => { keysRef.current[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    let lastTime = performance.now();
    let raf;

    const loop = (now) => {
      const dt = now - lastTime;
      lastTime = now;
      const s = stateRef.current;
      if (!s) return;

      s.timeLeft -= dt;
      if (s.timeLeft <= 0) {
        setRunning(false);
        setGameOver(true);
        submitScore('platformer', score);
        return;
      }

      const p = s.player;
      const mult = SPEED_MULT[speed];
      if (keysRef.current['ArrowLeft']) p.vx = -2 * mult;
      else if (keysRef.current['ArrowRight']) p.vx = 2 * mult;
      else p.vx = 0;

      if ((keysRef.current['ArrowUp'] || keysRef.current[' ']) && p.onGround) {
        p.vy = -6.5;
        p.onGround = false;
      }

      p.vy += 0.3; // gravity
      p.x += p.vx;
      p.y += p.vy;
      p.onGround = false;

      s.platforms.forEach((plat) => {
        if (p.x + p.w > plat.x && p.x < plat.x + plat.w && p.y + p.h > plat.y && p.y + p.h < plat.y + plat.h + p.vy + 1 && p.vy >= 0) {
          p.y = plat.y - p.h;
          p.vy = 0;
          p.onGround = true;
        }
      });

      if (p.x < 0) p.x = 0;
      if (p.x + p.w > W) p.x = W - p.w;
      if (p.y > H) { p.x = 20; p.y = 180; p.vy = 0; }

      s.coins.forEach((coin) => {
        if (!coin.taken && Math.abs(p.x - coin.x) < 14 && Math.abs(p.y - coin.y) < 14) {
          coin.taken = true;
          setScore((sc) => sc + 25);
        }
      });

      if (s.coins.every((c) => c.taken)) {
        setRunning(false);
        setGameOver(true);
        setWon(true);
        submitScore('platformer', score + 100);
        return;
      }

      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#8B6F47';
      s.platforms.forEach((plat) => ctx.fillRect(plat.x, plat.y, plat.w, plat.h));
      ctx.fillStyle = '#F2C879';
      s.coins.forEach((coin) => { if (!coin.taken) { ctx.beginPath(); ctx.arc(coin.x, coin.y, 6, 0, Math.PI * 2); ctx.fill(); } });
      ctx.fillStyle = colors.primary;
      // Head
      ctx.beginPath();
      ctx.arc(p.x + p.w / 2, p.y + 5, 5, 0, Math.PI * 2);
      ctx.fill();
      // Body
      ctx.fillRect(p.x + 2, p.y + 9, p.w - 4, p.h - 13);
      // Legs
      ctx.fillRect(p.x + 2, p.y + p.h - 4, 5, 4);
      ctx.fillRect(p.x + p.w - 7, p.y + p.h - 4, 5, 4);

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [running, score, speed]);

  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Score: {score} · Collect all coins!</p>
      <canvas ref={canvasRef} width={W} height={H} style={{ border: `2px solid ${colors.border}`, borderRadius: 8, maxWidth: '100%', background: colors.bg }} />
      {!running && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10 }}>
          {['slow', 'medium', 'fast'].map((s) => (
            <button key={s} onClick={() => setSpeed(s)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', border: `1px solid ${colors.border}`, borderRadius: 16, background: speed === s ? colors.primary : '#fff', color: speed === s ? '#fff' : colors.textMuted, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        {!running && (
          <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
            {gameOver ? t('gh_play_again') : t('gh_start')}
          </button>
        )}
      </div>
      {gameOver && <p style={{ color: won ? colors.primary : colors.danger, marginTop: 8 }}>{won ? 'You collected all coins! 🎉' : "Time's up!"} Score: {score}</p>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 12 }}>
        <button onTouchStart={() => keysRef.current['ArrowLeft'] = true} onTouchEnd={() => keysRef.current['ArrowLeft'] = false} style={{ padding: '10px 14px' }}>⬅️</button>
        <button onTouchStart={() => keysRef.current['ArrowUp'] = true} onTouchEnd={() => keysRef.current['ArrowUp'] = false} style={{ padding: '10px 14px' }}>{t('gh_jump')}</button>
        <button onTouchStart={() => keysRef.current['ArrowRight'] = true} onTouchEnd={() => keysRef.current['ArrowRight'] = false} style={{ padding: '10px 14px' }}>➡️</button>
      </div>
      <LeaderboardPanel game="platformer" />
    </div>
  );
}
// ==================== FIGHTER ====================

function FighterGame() {
  const canvasRef = useRef(null);
  const [gameOver, setGameOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [won, setWon] = useState(false);
  const [hp, setHp] = useState({ player: 100, ai: 100 });
  const [streakInfo, setStreakInfo] = useState(null);
  const [speed, setSpeed] = useState('medium');
  const stateRef = useRef(null);
  const keysRef = useRef({});
  const SPEED_MULT = { slow: 0.6, medium: 1, fast: 1.5 };

  useEffect(() => { fetchStreak('fighter').then((r) => { if (r) setStreakInfo(r); }); }, []);

  const W = 320, H = 180;
  const GROUND = H - 30;

  const startGame = () => {
    stateRef.current = {
      player: { x: 40, y: GROUND, hp: 100, facing: 1, attackCooldown: 0, attackType: null, attackFrame: 0 },
      ai: { x: W - 60, y: GROUND, hp: 100, facing: -1, attackCooldown: 0, attackType: null, attackFrame: 0, aiTimer: 0 },
    };
    setHp({ player: 100, ai: 100 });
    setGameOver(false);
    setWon(false);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const onKeyDown = (e) => { keysRef.current[e.key.toLowerCase()] = true; };
    const onKeyUp = (e) => { keysRef.current[e.key.toLowerCase()] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    const dist = (a, b) => Math.abs(a.x - b.x);

    const tryAttack = (attacker, defender, type) => {
      if (attacker.attackCooldown > 0) return;
      const range = type === 'punch' ? 30 : 40;
      const damage = type === 'punch' ? 6 : 10;
      attacker.attackType = type;
      attacker.attackFrame = 10;
      attacker.attackCooldown = type === 'punch' ? 20 : 35;
      if (dist(attacker, defender) < range) {
        defender.hp = Math.max(0, defender.hp - damage);
      }
    };

    let raf;
    const loop = () => {
      const s = stateRef.current;
      if (!s) return;
      const p = s.player, a = s.ai;

      if (keysRef.current['a']) { p.x -= 2 * SPEED_MULT[speed]; p.facing = -1; }
      if (keysRef.current['d']) { p.x += 2 * SPEED_MULT[speed]; p.facing = 1; }
      p.x = Math.max(10, Math.min(W - 30, p.x));

      if (p.attackCooldown > 0) p.attackCooldown--;
      if (p.attackFrame > 0) p.attackFrame--;
      if (keysRef.current['f']) tryAttack(p, a, 'punch');
      if (keysRef.current['g']) tryAttack(p, a, 'kick');

      // Simple AI
      a.aiTimer--;
      if (a.aiTimer <= 0) {
        a.aiTimer = 20 + Math.random() * 20;
        const d = dist(a, p);
        if (d > 35) {
          a.x += a.x > p.x ? -1.5 : 1.5;
        } else if (Math.random() > 0.4) {
          tryAttack(a, p, Math.random() > 0.5 ? 'punch' : 'kick');
        }
      }
      a.facing = a.x > p.x ? -1 : 1;
      a.x = Math.max(10, Math.min(W - 30, a.x));
      if (a.attackCooldown > 0) a.attackCooldown--;
      if (a.attackFrame > 0) a.attackFrame--;

      setHp({ player: p.hp, ai: a.hp });

      if (p.hp <= 0 || a.hp <= 0) {
        setRunning(false);
        setGameOver(true);
        const playerWon = a.hp <= 0 && p.hp > 0;
        setWon(playerWon);
        submitScore('fighter', playerWon ? 100 : 10);
        submitStreakResult('fighter', playerWon).then((r) => { if (r) setStreakInfo(r); });
        return;
      }

      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#8B6F47';
      ctx.fillRect(0, GROUND + 20, W, 10);

      const drawFighter = (f, color) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(f.x + 10, f.y - 33, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(f.x + 4, f.y - 26, 12, 20);
        ctx.fillRect(f.x + 4, f.y - 6, 5, 6);
        ctx.fillRect(f.x + 11, f.y - 6, 5, 6);
        ctx.fillRect(f.facing === 1 ? f.x + 14 : f.x - 4, f.y - 22, 8, 5);
        if (f.attackType && f.attackFrame > 0) {
          const k = f.attackFrame / 10;
          const swing = Math.sin((1 - k) * Math.PI);
          const dir = f.facing === 1 ? 1 : -1;
          if (f.attackType === 'punch') {
            const reach = 14 * swing;
            const ay = f.y - 20;
            const ax0 = f.facing === 1 ? f.x + 14 : f.x - 4;
            ctx.fillStyle = '#F2C879';
            ctx.fillRect(dir === 1 ? ax0 : ax0 - reach, ay, reach + 6, 5);
            ctx.beginPath();
            ctx.arc(dir === 1 ? ax0 + reach + 3 : ax0 - reach - 3, ay + 2, 4, 0, Math.PI * 2);
            ctx.fill();
          } else {
            const reach = 20 * swing;
            const ky2 = f.y - 8;
            const kx0 = f.facing === 1 ? f.x + 14 : f.x - 4;
            ctx.fillStyle = '#EF4444';
            ctx.fillRect(dir === 1 ? kx0 : kx0 - reach, ky2, reach + 8, 6);
            ctx.beginPath();
            ctx.arc(dir === 1 ? kx0 + reach + 4 : kx0 - reach - 4, ky2 + 3, 4, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      };
      drawFighter(p, colors.primary);
      drawFighter(a, colors.danger);

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [running, speed]);

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: W, margin: '0 auto 6px' }}>
        <div style={{ flex: 1, marginRight: 8 }}>
          <p style={{ fontSize: 11, margin: '0 0 2px' }}>{t('gh_you')}</p>
          <div style={{ background: '#F1EFE8', borderRadius: 6, height: 10, overflow: 'hidden' }}><div style={{ width: `${hp.player}%`, background: colors.primary, height: '100%' }} /></div>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 11, margin: '0 0 2px' }}>{t('gh_opp')}</p>
          <div style={{ background: '#F1EFE8', borderRadius: 6, height: 10, overflow: 'hidden' }}><div style={{ width: `${hp.ai}%`, background: colors.danger, height: '100%', marginLeft: 'auto' }} /></div>
        </div>
      </div>
      <canvas ref={canvasRef} width={W} height={H} style={{ border: `2px solid ${colors.border}`, borderRadius: 8, maxWidth: '100%', background: colors.bg }} />
      {!running && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10 }}>
          {['slow', 'medium', 'fast'].map((s) => (
            <button key={s} onClick={() => setSpeed(s)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', border: `1px solid ${colors.border}`, borderRadius: 16, background: speed === s ? colors.primary : '#fff', color: speed === s ? '#fff' : colors.textMuted, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        {!running && (
          <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
            {gameOver ? t('gh_play_again') : t('gh_start')}
          </button>
        )}
      </div>
      {gameOver && <p style={{ color: won ? colors.primary : colors.danger, marginTop: 8 }}>{won ? t('gh_won_fight') : t('gh_lost_fight')}</p>}
      {streakInfo && (
        <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
          🔥 Current streak: <b>{streakInfo.current_streak}</b>{t('gh_best')}<b>{streakInfo.best_streak}</b>
        </p>
      )}
      <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 8 }}>{t('gh_fight_keys')}</p>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        <button onTouchStart={() => keysRef.current['a'] = true} onTouchEnd={() => keysRef.current['a'] = false} style={{ padding: '10px 14px' }}>⬅️</button>
        <button onTouchStart={() => keysRef.current['d'] = true} onTouchEnd={() => keysRef.current['d'] = false} style={{ padding: '10px 14px' }}>➡️</button>
        <button onTouchStart={() => keysRef.current['f'] = true} onTouchEnd={() => keysRef.current['f'] = false} style={{ padding: '10px 14px' }}>{t('gh_punch_btn')}</button>
        <button onTouchStart={() => keysRef.current['g'] = true} onTouchEnd={() => keysRef.current['g'] = false} style={{ padding: '10px 14px' }}>{t('gh_kick_btn')}</button>
      </div>
      <LeaderboardPanel game="fighter" />
    </div>
  );
}
// ==================== UNO ====================

const UNO_COLORS = ['red', 'yellow', 'green', 'blue'];
const UNO_COLOR_HEX = { red: '#EF4444', yellow: '#F2C879', green: '#22C55E', blue: '#3B82F6', wild: '#374151' };

function buildUnoDeck() {
  const deck = [];
  UNO_COLORS.forEach((color) => {
    deck.push({ color, value: '0' });
    for (let n = 1; n <= 9; n++) { deck.push({ color, value: String(n) }); deck.push({ color, value: String(n) }); }
    ['skip', 'reverse', 'draw2'].forEach((v) => { deck.push({ color, value: v }); deck.push({ color, value: v }); });
  });
  for (let i = 0; i < 4; i++) { deck.push({ color: 'wild', value: 'wild' }); deck.push({ color: 'wild', value: 'wild_draw4' }); }
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

function unoCardLabel(card) {
  const labels = { skip: '🚫', reverse: '🔄', draw2: '+2', wild: '★', wild_draw4: '+4' };
  return labels[card.value] || card.value;
}

function UnoCard({ card, onClick, small }) {
  const size = small ? { w: 34, h: 48, fs: 14 } : { w: 46, h: 66, fs: 18 };
  return (
    <div onClick={onClick} style={{
      width: size.w, height: size.h, borderRadius: 8, background: UNO_COLOR_HEX[card.color] || '#374151',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size.fs, cursor: onClick ? 'pointer' : 'default',
      border: '2px solid rgba(255,255,255,0.5)', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    }}>
      {unoCardLabel(card)}
    </div>
  );
}

function UnoGame() {
  const [state, setState] = useState(null);
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [pickingColor, setPickingColor] = useState(null);
  const [msg, setMsg] = useState('');

  const startGame = () => {
    const deck = buildUnoDeck();
    const playerHand = deck.splice(0, 7);
    const aiHand = deck.splice(0, 7);
    let discard = [];
    while (deck.length) {
      const c = deck.shift();
      if (!isNaN(parseInt(c.value, 10)) && c.color !== 'wild') { discard = [c]; break; }
      deck.push(c);
    }
    setState({ playerHand, aiHand, drawPile: deck, discardPile: discard, currentColor: discard[0].color, turn: 'player' });
    setGameOver(false);
    setWon(false);
    setMsg('');
    setRunning(true);
  };

  const canPlay = (card, s) => {
    const top = s.discardPile[s.discardPile.length - 1];
    if (card.color === 'wild') return true;
    return card.color === s.currentColor || card.value === top.value;
  };

  const drawCards = (n, s) => {
    const drawn = [];
    for (let i = 0; i < n; i++) {
      if (s.drawPile.length === 0) break;
      drawn.push(s.drawPile.shift());
    }
    return drawn;
  };

  const applyCardEffect = (card, s, playerIsActor) => {
    let nextTurn = playerIsActor ? 'ai' : 'player';
    if (card.value === 'skip' || card.value === 'reverse') {
      nextTurn = playerIsActor ? 'player' : 'ai';
    } else if (card.value === 'draw2') {
      const target = playerIsActor ? 'aiHand' : 'playerHand';
      s[target] = [...s[target], ...drawCards(2, s)];
      nextTurn = playerIsActor ? 'player' : 'ai';
    } else if (card.value === 'wild_draw4') {
      const target = playerIsActor ? 'aiHand' : 'playerHand';
      s[target] = [...s[target], ...drawCards(4, s)];
      nextTurn = playerIsActor ? 'player' : 'ai';
    }
    return nextTurn;
  };

  const playPlayerCard = (index, chosenColor) => {
    setState((prev) => {
      const s = { ...prev, playerHand: [...prev.playerHand], discardPile: [...prev.discardPile] };
      const card = s.playerHand[index];
      if (!canPlay(card, s)) { setMsg(t('gh_card_no_match')); return prev; }

      s.playerHand.splice(index, 1);
      s.discardPile.push(card);
      s.currentColor = card.color === 'wild' ? (chosenColor || 'red') : card.color;
      setMsg('');

      if (s.playerHand.length === 0) {
        setRunning(false); setGameOver(true); setWon(true);
        submitScore('uno', 100);
        return s;
      }

      s.turn = applyCardEffect(card, s, true);
      return s;
    });
  };

  const drawPlayerCard = () => {
    setState((prev) => {
      const s = { ...prev, playerHand: [...prev.playerHand] };
      s.playerHand.push(...drawCards(1, s));
      s.turn = 'ai';
      return s;
    });
  };

  // AI turn effect
  useEffect(() => {
    if (!running || !state || state.turn !== 'ai') return;
    const timer = setTimeout(() => {
      setState((prev) => {
        const s = { ...prev, aiHand: [...prev.aiHand], discardPile: [...prev.discardPile] };
        const playableIdx = s.aiHand.findIndex((c) => canPlay(c, s));

        if (playableIdx === -1) {
          s.aiHand.push(...drawCards(1, s));
          s.turn = 'player';
          return s;
        }

        const card = s.aiHand[playableIdx];
        s.aiHand.splice(playableIdx, 1);
        s.discardPile.push(card);

        if (card.color === 'wild') {
          const counts = {};
          s.aiHand.forEach((c) => { if (c.color !== 'wild') counts[c.color] = (counts[c.color] || 0) + 1; });
          const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
          s.currentColor = best ? best[0] : UNO_COLORS[Math.floor(Math.random() * 4)];
        } else {
          s.currentColor = card.color;
        }

        if (s.aiHand.length === 0) {
          setRunning(false); setGameOver(true); setWon(false);
          submitScore('uno', 10);
          return s;
        }

        s.turn = applyCardEffect(card, s, false);
        return s;
      });
    }, 900);
    return () => clearTimeout(timer);
  }, [state, running]);

  const handleCardClick = (index) => {
    if (!state || state.turn !== 'player') return;
    const card = state.playerHand[index];
    if (!canPlay(card, state)) { setMsg(t('gh_card_no_match')); return; }
    if (card.color === 'wild') {
      setPickingColor(index);
    } else {
      playPlayerCard(index);
    }
  };

  const topCard = state?.discardPile[state.discardPile.length - 1];

  return (
    <div style={{ textAlign: 'center' }}>
      {!running && !state && (
        <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_start')}</button>
      )}

      {state && (
        <>
          <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>AI's hand: {state.aiHand.length} cards</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
            {state.aiHand.map((_, i) => <div key={i} style={{ width: 24, height: 34, borderRadius: 6, background: '#374151' }} />)}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, margin: '16px 0' }}>
            {topCard && <UnoCard card={{ ...topCard, color: state.currentColor === topCard.color ? topCard.color : state.currentColor }} />}
            <div onClick={running && state.turn === 'player' ? drawPlayerCard : undefined} style={{ width: 46, height: 66, borderRadius: 8, background: '#8B6F47', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, cursor: running && state.turn === 'player' ? 'pointer' : 'default', border: '2px solid rgba(255,255,255,0.5)' }}>
              Draw<br />({state.drawPile.length})
            </div>
          </div>

          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{running ? (state.turn === 'player' ? 'Your turn' : "AI's turn...") : ''}</p>
          {msg && <p style={{ fontSize: 12, color: colors.danger, marginBottom: 8 }}>{msg}</p>}

          {pickingColor !== null && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
              {UNO_COLORS.map((c) => (
                <div key={c} onClick={() => { playPlayerCard(pickingColor, c); setPickingColor(null); }} style={{ width: 36, height: 36, borderRadius: '50%', background: UNO_COLOR_HEX[c], cursor: 'pointer', border: '2px solid #fff', boxShadow: '0 0 0 1px #ccc' }} />
              ))}
            </div>
          )}

          <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>{t('gh_hand')}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
            {state.playerHand.map((c, i) => <UnoCard key={i} card={c} onClick={() => handleCardClick(i)} />)}
          </div>
        </>
      )}

      {gameOver && (
        <>
          <p style={{ color: won ? colors.primary : colors.danger, marginTop: 8, fontWeight: 600 }}>{won ? t('gh_won_uno') : t('gh_lost_uno')}</p>
          <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 8 }}>{t('gh_play_again')}</button>
        </>
      )}
      <LeaderboardPanel game="uno" />
    </div>
  );
}

// ==================== MAIN HUB ====================

// ==================== Chess (vs computer) ====================

const CH_PVAL = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 20000 };
const CH_GL = { wk: '\u2654', wq: '\u2655', wr: '\u2656', wb: '\u2657', wn: '\u2658', wp: '\u2659', bk: '\u265A', bq: '\u265B', br: '\u265C', bb: '\u265D', bn: '\u265E', bp: '\u265F' };
const CH_FILLED = { k: '\u265A', q: '\u265B', r: '\u265C', b: '\u265D', n: '\u265E', p: '\u265F' };
const CH_IMG = 'https://cdn.jsdelivr.net/npm/@chrisoakman/chessboardjs@1.0.0/dist/img/chesspieces/wikipedia/';
const CH_THEMES = {
  walnut: { label: 'Walnut', l: '#FAEEDA', d: '#BA7517', ll: '#854F0B', dl: '#FAEEDA' },
  forest: { label: 'Forest', l: '#EAF3DE', d: '#639922', ll: '#3B6D11', dl: '#EAF3DE' },
  ocean: { label: 'Ocean', l: '#E6F1FB', d: '#378ADD', ll: '#185FA5', dl: '#E6F1FB' },
  slate: { label: 'Slate', l: '#F1EFE8', d: '#888780', ll: '#5F5E5A', dl: '#F1EFE8' },
  emerald: { label: 'Emerald', l: '#E1F5EE', d: '#0F6E56', ll: '#04342C', dl: '#E1F5EE' },
};
const CH_PST = {
  p: [0,0,0,0,0,0,0,0,50,50,50,50,50,50,50,50,10,10,20,30,30,20,10,10,5,5,10,25,25,10,5,5,0,0,0,20,20,0,0,0,5,-5,-10,0,0,-10,-5,5,5,10,10,-20,-20,10,10,5,0,0,0,0,0,0,0,0],
  n: [-50,-40,-30,-30,-30,-30,-40,-50,-40,-20,0,0,0,0,-20,-40,-30,0,10,15,15,10,0,-30,-30,5,15,20,20,15,5,-30,-30,0,15,20,20,15,0,-30,-30,5,10,15,15,10,5,-30,-40,-20,0,5,5,0,-20,-40,-50,-40,-30,-30,-30,-30,-40,-50],
  b: [-20,-10,-10,-10,-10,-10,-10,-20,-10,0,0,0,0,0,0,-10,-10,0,5,10,10,5,0,-10,-10,5,5,10,10,5,5,-10,-10,0,10,10,10,10,0,-10,-10,10,10,10,10,10,10,-10,-10,5,0,0,0,0,5,-10,-20,-10,-10,-10,-10,-10,-10,-20],
  r: [0,0,0,0,0,0,0,0,5,10,10,10,10,10,10,5,-5,0,0,0,0,0,0,-5,-5,0,0,0,0,0,0,-5,-5,0,0,0,0,0,0,-5,-5,0,0,0,0,0,0,-5,-5,0,0,0,0,0,0,-5,0,0,0,5,5,0,0,0],
  q: [-20,-10,-10,-5,-5,-10,-10,-20,-10,0,0,0,0,0,0,-10,-10,0,5,5,5,5,0,-10,-5,0,5,5,5,5,0,-5,0,0,5,5,5,5,0,-5,-10,5,5,5,5,5,0,-10,-10,0,5,0,0,0,0,-10,-20,-10,-10,-5,-5,-10,-10,-20],
  k: [-30,-40,-40,-50,-50,-40,-40,-30,-30,-40,-40,-50,-50,-40,-40,-30,-30,-40,-40,-50,-50,-40,-40,-30,-30,-40,-40,-50,-50,-40,-40,-30,-20,-30,-30,-40,-40,-30,-30,-20,-10,-20,-20,-20,-20,-20,-20,-10,20,20,0,0,0,0,20,20,20,30,10,0,0,10,30,20],
};
const chRc = (i) => [i >> 3, i & 7];
const chInB = (r, c) => r >= 0 && r < 8 && c >= 0 && c < 8;
function chStartBoard() {
  const b = new Array(64).fill(null);
  const back = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'];
  for (let i = 0; i < 8; i++) {
    b[i] = { t: back[i], c: 'b' }; b[8 + i] = { t: 'p', c: 'b' };
    b[48 + i] = { t: 'p', c: 'w' }; b[56 + i] = { t: back[i], c: 'w' };
  }
  return b;
}
function chInit() {
  return { b: chStartBoard(), turn: 'w', cast: { wk: true, wq: true, bk: true, bq: true }, ep: -1, hist: [], caps: { w: [], b: [] }, moves: [], over: null };
}
function chAttacked(b, sq, by) {
  const [r, c] = chRc(sq);
  const pd = by === 'w' ? 1 : -1;
  for (const dc of [-1, 1]) { const rr = r + pd, cc = c + dc; if (chInB(rr, cc)) { const p = b[rr * 8 + cc]; if (p && p.c === by && p.t === 'p') return true; } }
  for (const [dr, dc] of [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]]) { const rr = r + dr, cc = c + dc; if (chInB(rr, cc)) { const p = b[rr * 8 + cc]; if (p && p.c === by && p.t === 'n') return true; } }
  for (const [dr, dc] of [[-1,-1],[-1,1],[1,-1],[1,1]]) { let rr = r + dr, cc = c + dc; while (chInB(rr, cc)) { const p = b[rr * 8 + cc]; if (p) { if (p.c === by && (p.t === 'b' || p.t === 'q')) return true; break; } rr += dr; cc += dc; } }
  for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]]) { let rr = r + dr, cc = c + dc; while (chInB(rr, cc)) { const p = b[rr * 8 + cc]; if (p) { if (p.c === by && (p.t === 'r' || p.t === 'q')) return true; break; } rr += dr; cc += dc; } }
  for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) { if (!dr && !dc) continue; const rr = r + dr, cc = c + dc; if (chInB(rr, cc)) { const p = b[rr * 8 + cc]; if (p && p.c === by && p.t === 'k') return true; } }
  return false;
}
function chKingSq(b, c) { for (let i = 0; i < 64; i++) { const p = b[i]; if (p && p.t === 'k' && p.c === c) return i; } return -1; }
function chAddPawn(ms, f, t, promo) { if (promo) { for (const pr of ['q', 'r', 'b', 'n']) ms.push({ f, t, promo: pr }); } else ms.push({ f, t }); }
function chPseudo(st, side) {
  const b = st.b, ms = [];
  for (let i = 0; i < 64; i++) {
    const p = b[i]; if (!p || p.c !== side) continue;
    const [r, c] = chRc(i);
    if (p.t === 'p') {
      const d = side === 'w' ? -1 : 1, startR = side === 'w' ? 6 : 1, promR = side === 'w' ? 0 : 7;
      const f1 = (r + d) * 8 + c;
      if (chInB(r + d, c) && !b[f1]) { chAddPawn(ms, i, f1, r + d === promR); if (r === startR && !b[(r + 2 * d) * 8 + c]) ms.push({ f: i, t: (r + 2 * d) * 8 + c, dp: true }); }
      for (const dc of [-1, 1]) {
        const rr = r + d, cc = c + dc; if (!chInB(rr, cc)) continue;
        const tsq = rr * 8 + cc, tp = b[tsq];
        if (tp && tp.c !== side) chAddPawn(ms, i, tsq, rr === promR);
        else if (tsq === st.ep) ms.push({ f: i, t: tsq, ep: true });
      }
    } else if (p.t === 'n') {
      for (const [dr, dc] of [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]]) { const rr = r + dr, cc = c + dc; if (chInB(rr, cc)) { const tp = b[rr * 8 + cc]; if (!tp || tp.c !== side) ms.push({ f: i, t: rr * 8 + cc }); } }
    } else if (p.t === 'k') {
      for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) { if (!dr && !dc) continue; const rr = r + dr, cc = c + dc; if (chInB(rr, cc)) { const tp = b[rr * 8 + cc]; if (!tp || tp.c !== side) ms.push({ f: i, t: rr * 8 + cc }); } }
      const home = side === 'w' ? 56 : 0, opp = side === 'w' ? 'b' : 'w';
      if (i === home + 4 && !chAttacked(b, i, opp)) {
        if (st.cast[side + 'k'] && !b[home + 5] && !b[home + 6] && !chAttacked(b, home + 5, opp) && !chAttacked(b, home + 6, opp) && b[home + 7] && b[home + 7].t === 'r' && b[home + 7].c === side) ms.push({ f: i, t: home + 6, castle: 'k' });
        if (st.cast[side + 'q'] && !b[home + 3] && !b[home + 2] && !b[home + 1] && !chAttacked(b, home + 3, opp) && !chAttacked(b, home + 2, opp) && b[home] && b[home].t === 'r' && b[home].c === side) ms.push({ f: i, t: home + 2, castle: 'q' });
      }
    } else {
      const dirs = p.t === 'b' ? [[-1,-1],[-1,1],[1,-1],[1,1]] : p.t === 'r' ? [[-1,0],[1,0],[0,-1],[0,1]] : [[-1,-1],[-1,1],[1,-1],[1,1],[-1,0],[1,0],[0,-1],[0,1]];
      for (const [dr, dc] of dirs) { let rr = r + dr, cc = c + dc; while (chInB(rr, cc)) { const tp = b[rr * 8 + cc]; if (!tp) ms.push({ f: i, t: rr * 8 + cc }); else { if (tp.c !== side) ms.push({ f: i, t: rr * 8 + cc }); break; } rr += dr; cc += dc; } }
    }
  }
  return ms;
}
function chApply(st, m) {
  const b = st.b.slice(), p = b[m.f];
  const nst = { b, turn: st.turn === 'w' ? 'b' : 'w', cast: { ...st.cast }, ep: -1 };
  let captured = b[m.t];
  b[m.t] = { ...p }; b[m.f] = null;
  if (m.dp) nst.ep = (m.f + m.t) / 2;
  if (m.ep) { const capSq = st.turn === 'w' ? m.t + 8 : m.t - 8; captured = b[capSq]; b[capSq] = null; }
  if (m.promo) b[m.t] = { t: m.promo, c: p.c };
  if (m.castle) { const home = p.c === 'w' ? 56 : 0; if (m.castle === 'k') { b[home + 5] = b[home + 7]; b[home + 7] = null; } else { b[home + 3] = b[home]; b[home] = null; } }
  if (p.t === 'k') { nst.cast[p.c + 'k'] = false; nst.cast[p.c + 'q'] = false; }
  if (p.t === 'r') { if (m.f === (p.c === 'w' ? 63 : 7)) nst.cast[p.c + 'k'] = false; if (m.f === (p.c === 'w' ? 56 : 0)) nst.cast[p.c + 'q'] = false; }
  if (captured && captured.t === 'r') { if (m.t === 63) nst.cast.wk = false; if (m.t === 56) nst.cast.wq = false; if (m.t === 7) nst.cast.bk = false; if (m.t === 0) nst.cast.bq = false; }
  nst.cap = captured || null;
  return nst;
}
function chLegal(st, side) {
  return chPseudo(st, side).filter((m) => { const n = chApply(st, m); return !chAttacked(n.b, chKingSq(n.b, side), side === 'w' ? 'b' : 'w'); });
}
function chEval(b) {
  let s = 0;
  for (let i = 0; i < 64; i++) { const p = b[i]; if (!p) continue; const idx = p.c === 'w' ? i : 63 - i; const v = CH_PVAL[p.t] + CH_PST[p.t][idx]; s += p.c === 'w' ? v : -v; }
  return s;
}
function chSearch(st, depth, alpha, beta, side) {
  const ms = chLegal(st, side);
  if (!ms.length) { const chk = chAttacked(st.b, chKingSq(st.b, side), side === 'w' ? 'b' : 'w'); return { score: chk ? (side === 'w' ? -99999 - depth : 99999 + depth) : 0 }; }
  if (depth === 0) return { score: chEval(st.b) };
  ms.sort((a, b2) => ((st.b[b2.t] ? CH_PVAL[st.b[b2.t].t] : 0) - (st.b[a.t] ? CH_PVAL[st.b[a.t].t] : 0)));
  let best = null;
  if (side === 'w') {
    let v = -Infinity;
    for (const m of ms) { const r = chSearch(chApply(st, m), depth - 1, alpha, beta, 'b'); if (r.score > v) { v = r.score; best = m; } alpha = Math.max(alpha, v); if (beta <= alpha) break; }
    return { score: v, move: best };
  }
  let v = Infinity;
  for (const m of ms) { const r = chSearch(chApply(st, m), depth - 1, alpha, beta, 'w'); if (r.score < v) { v = r.score; best = m; } beta = Math.min(beta, v); if (beta <= alpha) break; }
  return { score: v, move: best };
}
const chSqName = (i) => { const [r, c] = chRc(i); return 'abcdefgh'[c] + (8 - r); };
function chNotate(st, m) {
  const p = st.b[m.f];
  if (m.castle) return m.castle === 'k' ? 'O-O' : 'O-O-O';
  let s = p.t !== 'p' ? p.t.toUpperCase() : '';
  if (st.b[m.t] || m.ep) { if (p.t === 'p') s += chSqName(m.f)[0]; s += 'x'; }
  s += chSqName(m.t);
  if (m.promo) s += '=' + m.promo.toUpperCase();
  return s;
}

function ChessGame() {
  const [S, setS] = useState(chInit);
  const [sel, setSel] = useState(null);
  const [selMoves, setSelMoves] = useState([]);
  const [theme, setTheme] = useState('walnut');
  const [pieceStyle, setPieceStyle] = useState('tournament');
  const [difficulty, setDifficulty] = useState(2);
  const [humanSide, setHumanSide] = useState('w');
  const [thinking, setThinking] = useState(false);
  const [imgFail, setImgFail] = useState(false);

  const commitMove = (st, m, hSide) => {
    const note = chNotate(st, m);
    const prev = { b: st.b, turn: st.turn, cast: { ...st.cast }, ep: st.ep, capsW: st.caps.w.length, capsB: st.caps.b.length };
    const n = chApply(st, m);
    const caps = { w: st.caps.w.slice(), b: st.caps.b.slice() };
    if (n.cap) caps[n.cap.c === 'w' ? 'b' : 'w'].push(n.cap);
    const next = { b: n.b, turn: n.turn, cast: n.cast, ep: n.ep, hist: [...st.hist, prev], caps, moves: [...st.moves, note], over: null };
    const ms = chLegal(next, next.turn);
    const chk = chAttacked(next.b, chKingSq(next.b, next.turn), next.turn === 'w' ? 'b' : 'w');
    if (!ms.length) {
      if (chk) {
        const humanWon = next.turn !== hSide;
        next.over = humanWon ? t('gh_mate_win') : t('gh_mate_lose');
        submitScore('chess', humanWon ? 100 : 10);
        submitStreakResult('chess', humanWon);
      } else {
        next.over = 'Stalemate — draw';
        submitScore('chess', 50);
      }
    } else if (chk) {
      next.moves = next.moves.slice(0, -1).concat(next.moves[next.moves.length - 1] + '+');
    }
    setS(next);
    return next;
  };

  const aiMove = (st, hSide, diff) => {
    if (st.over || st.turn === hSide) return;
    setThinking(true);
    setTimeout(() => {
      let m;
      if (diff === 1) {
        const ms = chLegal(st, st.turn);
        ms.sort((a, b2) => ((st.b[b2.t] ? CH_PVAL[st.b[b2.t].t] : 0) - (st.b[a.t] ? CH_PVAL[st.b[a.t].t] : 0)));
        m = Math.random() < 0.5 ? ms[0] : ms[Math.floor(Math.random() * ms.length)];
      } else {
        m = chSearch(st, diff, -Infinity, Infinity, st.turn).move;
      }
      setThinking(false);
      if (m) commitMove(st, m, hSide);
    }, 60);
  };

  const clickSq = (i) => {
    if (S.over || thinking || S.turn !== humanSide) return;
    const p = S.b[i];
    const mv = selMoves.find((m) => m.f === sel && m.t === i && (!m.promo || m.promo === 'q'));
    if (mv) {
      setSel(null); setSelMoves([]);
      const next = commitMove(S, mv, humanSide);
      if (!next.over) aiMove(next, humanSide, difficulty);
      return;
    }
    if (p && p.c === humanSide) { setSel(i); setSelMoves(chLegal(S, humanSide).filter((m) => m.f === i)); }
    else { setSel(null); setSelMoves([]); }
  };

  const newGame = (side) => {
    const st = chInit();
    setS(st); setSel(null); setSelMoves([]);
    if (side !== 'w') aiMove(st, side, difficulty);
  };

  const undo = () => {
    if (!S.hist.length || thinking) return;
    let n = S.turn !== humanSide ? 1 : 2;
    n = Math.min(n, S.hist.length);
    let cur = S;
    for (let k = 0; k < n; k++) {
      const prev = cur.hist[cur.hist.length - 1];
      cur = {
        b: prev.b, turn: prev.turn, cast: prev.cast, ep: prev.ep,
        hist: cur.hist.slice(0, -1),
        caps: { w: cur.caps.w.slice(0, prev.capsW), b: cur.caps.b.slice(0, prev.capsB) },
        moves: cur.moves.slice(0, -1), over: null,
      };
    }
    setS(cur); setSel(null); setSelMoves([]);
  };

  const th = CH_THEMES[theme];
  const flip = humanSide === 'b';
  const kSq = chKingSq(S.b, S.turn);
  const inCheck = chAttacked(S.b, kSq, S.turn === 'w' ? 'b' : 'w');
  const ctrl = { padding: '6px 8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' };

  const renderPiece = (p) => {
    if (pieceStyle === 'tournament' && !imgFail) {
      return <img src={CH_IMG + p.c + p.t.toUpperCase() + '.png'} alt={p.c + p.t} onError={() => setImgFail(true)} style={{ width: '84%', height: '84%', pointerEvents: 'none' }} />;
    }
    if (pieceStyle === 'letters') {
      const w = p.c === 'w';
      return (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '64%', height: '64%', borderRadius: '50%', fontSize: 'clamp(13px,3vw,22px)', fontWeight: 700, background: w ? '#FCFCFA' : '#1a1a18', color: w ? '#1a1a18' : '#FCFCFA', border: '2px solid #1a1a18' }}>
          {p.t === 'n' ? 'N' : p.t.toUpperCase()}
        </span>
      );
    }
    const ink = pieceStyle === 'ink';
    const glyph = ink ? CH_FILLED[p.t] : CH_GL[p.c + p.t];
    const st = { fontSize: 'clamp(26px,6.5vw,46px)', lineHeight: 1 };
    if (p.c === 'w') { st.color = '#FCFCFA'; st.WebkitTextStroke = ink ? '1px #5F5E5A' : '1.4px #2C2C2A'; st.paintOrder = 'stroke'; }
    else st.color = '#1a1a18';
    return <span style={st}>{glyph}</span>;
  };

  const status = S.over ? S.over
    : thinking ? 'Computer is thinking…'
    : S.turn === humanSide ? (inCheck ? t('gh_move_check') : t('gh_move'))
    : 'Computer to move';

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <button style={ctrl} onClick={() => newGame(humanSide)}>{t('gh_new')}</button>
        <button style={ctrl} onClick={undo}>{t('gh_undo')}</button>
        <select style={ctrl} value={difficulty} onChange={(e) => setDifficulty(parseInt(e.target.value, 10))}>
          <option value={1}>{t('gh_easy')}</option>
          <option value={2}>{t('gh_med')}</option>
          <option value={3}>{t('gh_hard')}</option>
        </select>
        <select style={ctrl} value={humanSide} onChange={(e) => { const side = e.target.value; setHumanSide(side); const st = chInit(); setS(st); setSel(null); setSelMoves([]); if (side === 'b') aiMove(st, side, difficulty); }}>
          <option value="w">{t('gh_white')}</option>
          <option value="b">{t('gh_black')}</option>
        </select>
        <select style={ctrl} value={theme} onChange={(e) => setTheme(e.target.value)}>
          {Object.keys(CH_THEMES).map((k) => <option key={k} value={k}>{CH_THEMES[k].label} board</option>)}
        </select>
        <select style={ctrl} value={pieceStyle} onChange={(e) => setPieceStyle(e.target.value)}>
          <option value="tournament">{t('gh_pc_t')}</option>
          <option value="classic">{t('gh_pc_c')}</option>
          <option value="ink">{t('gh_pc_i')}</option>
          <option value="letters">{t('gh_pc_l')}</option>
        </select>
      </div>
      <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px', color: S.over ? colors.primary : '#2C2C2A' }}>{status}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', width: '100%', maxWidth: 560, aspectRatio: '1', border: `0.5px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden', userSelect: 'none' }}>
        {Array.from({ length: 64 }, (_, vis) => {
          const i = flip ? 63 - vis : vis;
          const [r, c] = chRc(i);
          const light = (r + c) % 2 === 0;
          let bg = light ? th.l : th.d;
          if (sel === i) bg = '#5DCAA5';
          if (inCheck && i === kSq) bg = '#F09595';
          const p = S.b[i];
          const isTarget = selMoves.some((m) => m.f === sel && m.t === i);
          return (
            <div key={i} onClick={() => clickSq(i)} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', aspectRatio: '1', background: bg }}>
              {p && renderPiece(p)}
              {isTarget && (p
                ? <div style={{ position: 'absolute', inset: 3, border: '3px solid #0F6E56', borderRadius: 8, pointerEvents: 'none' }} />
                : <div style={{ position: 'absolute', width: '24%', height: '24%', borderRadius: '50%', background: '#0F6E56', opacity: 0.55, pointerEvents: 'none' }} />)}
              {c === (flip ? 7 : 0) && <span style={{ position: 'absolute', top: 3, left: 4, fontSize: 10, fontWeight: 600, color: light ? th.ll : th.dl }}>{8 - r}</span>}
              {r === (flip ? 0 : 7) && <span style={{ position: 'absolute', bottom: 2, right: 4, fontSize: 10, fontWeight: 600, color: light ? th.ll : th.dl }}>{'abcdefgh'[c]}</span>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 560, margin: '6px 0 10px', fontSize: 18, minHeight: 24, color: '#5F5E5A' }}>
        <span>{S.caps.b.map((p) => CH_GL['w' + p.t]).join('')}</span>
        <span>{S.caps.w.map((p) => CH_FILLED[p.t]).join('')}</span>
      </div>
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>{t('gh_moves')}</div>
      <div style={{ fontSize: 12, fontFamily: 'monospace', lineHeight: 1.8, maxHeight: 130, overflowY: 'auto', maxWidth: 560, color: '#5F5E5A' }}>
        {S.moves.length === 0 ? <span style={{ color: colors.textMuted }}>{t('gh_no_moves')}</span>
          : Array.from({ length: Math.ceil(S.moves.length / 2) }, (_, k) => (
            <div key={k}>{k + 1}. {S.moves[k * 2]} {S.moves[k * 2 + 1] || ''}</div>
          ))}
      </div>
      <LeaderboardPanel game="chess" />
    </div>
  );
}


const GAMES_LIST = [
  { key: 'snake', label: '🐍 Snake', color: '#0F6E56' },
  { key: 'tetris', label: '🧱 Tetris', color: '#0EA5E9' },
  { key: 'platformer', label: '🏃 Platformer', color: '#8B6F47' },
  { key: 'fighter', label: '🥊 Fighter', color: '#A32D2D' },
  { key: 'uno', label: '🃏 UNO', color: '#22C55E' },
  { key: 'memory', label: '🧠 Memory Match', color: '#0D9488' },
  { key: 'penalty', label: '⚽ Penalty Shootout', color: '#15803D' },
  { key: 'chess', label: '♟️ Chess', color: '#0F6E56' },
  { key: 'rummy', label: '🏏 Rummy', color: '#7C3AED' },
  { key: 'sudoku', label: '🔢 Sudoku', color: '#DB2777' },
  { key: 'tictactoe', label: '⭕ Tic Tac Toe', color: '#0F6E56' },
  { key: 'rps', label: '✊ Rock Paper Scissors', color: '#8B5CF6' },
  { key: 'simon', label: '🎨 Simon Says', color: '#0EA5E9' },
  { key: 'whackamole', label: '🔨 Whack a Mole', color: '#A32D2D' },
  { key: 'numguess', label: '🔢 Number Guess', color: '#15803D' },
  { key: 'wordscramble', label: '🔤 Word Scramble', color: '#C0392B' },
  { key: 'slidepuzzle', label: '🧩 Sliding Puzzle', color: '#0D9488' },
  { key: 'connect4', label: '🔴 Connect Four', color: '#1d4ed8' },
  { key: 'oddoneout', label: '🍎 Odd One Out', color: '#DB2777' },
  { key: 'colormatch', label: '🌈 Color Match', color: '#7C3AED' },
  { key: 'chessp2p', label: '♟️🌐 Chess (Live)', color: '#0F6E56' },
  { key: 'unop2p', label: '🃏🌐 UNO Multiplayer', color: '#22C55E' },
];

// ==================== MEMORY MATCH ====================

const MEMORY_ICONS = ['🍎', '🍌', '🍇', '🍓', '🍒', '🍑', '🥝', '🍉'];

function MemoryMatchGame() {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [running, setRunning] = useState(false);
  const [won, setWon] = useState(false);

  const startGame = () => {
    const pairs = [...MEMORY_ICONS, ...MEMORY_ICONS]
      .map((icon, i) => ({ id: i, icon }))
      .sort(() => Math.random() - 0.5);
    setCards(pairs);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setWon(false);
    setRunning(true);
  };

  const handleCardClick = (id) => {
    if (flipped.length === 2 || flipped.includes(id) || matched.includes(id)) return;
    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = newFlipped;
      const cardA = cards.find((c) => c.id === a);
      const cardB = cards.find((c) => c.id === b);
      if (cardA.icon === cardB.icon) {
        setTimeout(() => {
          setMatched((prev) => {
            const next = [...prev, a, b];
            if (next.length === cards.length) {
              setRunning(false);
              setWon(true);
              const finalMoves = moves + 1;
              submitScore('memory', Math.max(1000 - finalMoves * 10, 10));
            }
            return next;
          });
          setFlipped([]);
        }, 500);
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Moves: {moves}</p>
      {running ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxWidth: 320, margin: '0 auto' }}>
          {cards.map((card) => {
            const isFlipped = flipped.includes(card.id) || matched.includes(card.id);
            return (
              <div
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                style={{
                  aspectRatio: '1', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 28, cursor: 'pointer', userSelect: 'none',
                  background: isFlipped ? '#fff' : colors.primary,
                  border: `2px solid ${colors.border}`,
                  transition: 'background 0.2s',
                }}
              >
                {isFlipped ? card.icon : ''}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontSize: 40 }}>🧠</p>
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {won ? t('gh_play_again') : running ? t('gh_restart') : t('gh_start')}
        </button>
      </div>
      {won && <p style={{ color: colors.primary, marginTop: 8 }}>Solved in {moves} moves! 🎉</p>}
      <LeaderboardPanel game="memory" />
    </div>
  );
}

// ==================== PENALTY SHOOTOUT ====================

function PenaltyShootoutGame() {
  const canvasRef = useRef(null);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [result, setResult] = useState(null); // 'goal' | 'saved' | null
  const [indicatorX, setIndicatorX] = useState(0);
  const stateRef = useRef(null);
  const TOTAL_ROUNDS = 5;
  const W = 320, H = 200;

  const startGame = () => {
    setRound(0);
    setScore(0);
    setGameOver(false);
    setResult(null);
    setRunning(true);
    startRound();
  };

  const startRound = () => {
    stateRef.current = { dir: 1, x: 40 };
    setResult(null);
    const move = () => {
      const s = stateRef.current;
      if (!s) return;
      s.x += s.dir * 4;
      if (s.x > W - 40) s.dir = -1;
      if (s.x < 40) s.dir = 1;
      setIndicatorX(s.x);
      s.raf = requestAnimationFrame(move);
    };
    stateRef.current.raf = requestAnimationFrame(move);
  };

  useEffect(() => {
    if (running && round === 0) startRound();
    return () => { if (stateRef.current?.raf) cancelAnimationFrame(stateRef.current.raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const shoot = () => {
    if (result) return;
    if (stateRef.current?.raf) cancelAnimationFrame(stateRef.current.raf);
    const goalCenter = W / 2;
    const dist = Math.abs(indicatorX - goalCenter);
    const isGoal = dist > 60; // scoring when indicator is away from keeper's center guess
    setResult(isGoal ? 'goal' : 'saved');
    if (isGoal) setScore((sc) => sc + 1);

    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= TOTAL_ROUNDS) {
        setRunning(false);
        setGameOver(true);
        submitScore('penalty', (isGoal ? score + 1 : score) * 20);
      } else {
        setRound(nextRound);
        startRound();
      }
    }, 900);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#15803D';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(0,0,0,0.06)';
    for (let i = 0; i < 5; i++) ctx.fillRect(0, 95 + i * 20, W, 10);

    const gx = 20, gy = 20, gw = W - 40, gh = 55;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 5;
    ctx.strokeRect(gx, gy, gw, gh);
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1;
    for (let x = gx; x <= gx + gw; x += 12) { ctx.beginPath(); ctx.moveTo(x, gy); ctx.lineTo(x, gy + gh); ctx.stroke(); }
    for (let y = gy; y <= gy + gh; y += 10) { ctx.beginPath(); ctx.moveTo(gx, y); ctx.lineTo(gx + gw, y); ctx.stroke(); }

    const kx = W / 2, ky = gy + gh - 8;
    ctx.fillStyle = '#facc15';
    ctx.fillRect(kx - 10, ky - 18, 20, 20);
    ctx.fillStyle = '#f1c27d';
    ctx.beginPath(); ctx.arc(kx, ky - 24, 7, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#facc15';
    ctx.fillRect(kx - 19, ky - 15, 9, 6);
    ctx.fillRect(kx + 10, ky - 15, 9, 6);
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(kx - 9, ky + 2, 7, 11);
    ctx.fillRect(kx + 2, ky + 2, 7, 11);

    ctx.fillStyle = result === 'goal' ? '#22c55e' : result === 'saved' ? '#ef4444' : '#fff';
    ctx.beginPath();
    ctx.arc(indicatorX, 150, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1f2937'; ctx.lineWidth = 1; ctx.stroke();
  }, [indicatorX, result]);

  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>⚽ Round {Math.min(round + 1, TOTAL_ROUNDS)}/{TOTAL_ROUNDS} · Score: {score}</p>
      <canvas ref={canvasRef} width={W} height={H} style={{ border: `2px solid ${colors.border}`, borderRadius: 8, maxWidth: '100%', background: colors.bg }} />
      {result && <p style={{ fontWeight: 600, color: result === 'goal' ? '#22c55e' : '#ef4444', marginTop: 8 }}>{result === 'goal' ? '⚽ GOAL!' : '🧤 Saved!'}</p>}
      <div style={{ marginTop: 10 }}>
        {!running && !gameOver && (
          <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_start')}</button>
        )}
        {running && !result && (
          <button onClick={shoot} style={{ padding: '12px 32px', fontSize: 15, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
            ⚽ Shoot!
          </button>
        )}
        {gameOver && (
          <button onClick={startGame} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_play_again')}</button>
        )}
      </div>
      <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>{t('gh_shoot_help')}</p>
      {gameOver && <p style={{ color: colors.primary, marginTop: 8 }}>Final: {score}/{TOTAL_ROUNDS} goals!</p>}
      <LeaderboardPanel game="penalty" />
    </div>
  );
}

// ==================== MATCH PREDICTOR ====================

function MatchPredictorGame() {
  const [tab, setTab] = useState('upcoming');
  const [upcoming, setUpcoming] = useState([]);
  const [results, setResults] = useState([]);
  const [board, setBoard] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, l] = await Promise.all([
        fetch(`${GAME_API_BASE}/match-prediction/upcoming/`, { headers: authHeaders() }).then((res) => res.json()),
        fetch(`${GAME_API_BASE}/match-prediction/results/`, { headers: authHeaders() }).then((res) => res.json()),
        fetch(`${GAME_API_BASE}/match-prediction/leaderboard/`).then((res) => res.json()),
      ]);
      setUpcoming(u.matches || []);
      setResults(r.matches || []);
      setBoard(l.leaderboard || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setPred = (matchId, field, value) => {
    setPredictions((prev) => ({ ...prev, [matchId]: { ...prev[matchId], [field]: value } }));
  };

  const submitPrediction = async (matchId) => {
    const pred = predictions[matchId];
    if (!pred || pred.home === undefined || pred.away === undefined) return;
    try {
      await fetch(`${GAME_API_BASE}/match-prediction/predict/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ match_id: matchId, predicted_home: pred.home, predicted_away: pred.away }),
      });
      load();
    } catch (e) {}
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {['upcoming', 'results', 'leaderboard'].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, textTransform: 'capitalize', border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'none', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer' }}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('gh_loading')}</p>
      ) : tab === 'upcoming' ? (
        upcoming.length === 0 ? (
          <p style={{ textAlign: 'center', color: colors.textMuted, padding: '2rem 0' }}>{t('gh_no_upcoming')}</p>
        ) : (
          upcoming.map((m) => (
            <div key={m.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 8 }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>{m.home_team} vs {m.away_team}</p>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 8px' }}>{new Date(m.match_date).toLocaleString()}</p>
              {m.my_prediction ? (
                <p style={{ fontSize: 13, color: colors.primary, fontWeight: 600 }}>Your prediction: {m.my_prediction.home} - {m.my_prediction.away}</p>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" min="0" placeholder="0" onChange={(e) => setPred(m.id, 'home', parseInt(e.target.value) || 0)} style={{ width: 44, padding: 6, textAlign: 'center', border: `0.5px solid ${colors.border}`, borderRadius: 6 }} />
                  <span>-</span>
                  <input type="number" min="0" placeholder="0" onChange={(e) => setPred(m.id, 'away', parseInt(e.target.value) || 0)} style={{ width: 44, padding: 6, textAlign: 'center', border: `0.5px solid ${colors.border}`, borderRadius: 6 }} />
                  <button onClick={() => submitPrediction(m.id)} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
                    Predict
                  </button>
                </div>
              )}
            </div>
          ))
        )
      ) : tab === 'results' ? (
        results.length === 0 ? (
          <p style={{ textAlign: 'center', color: colors.textMuted, padding: '2rem 0' }}>{t('gh_no_finished')}</p>
        ) : (
          results.map((m) => (
            <div key={m.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 8 }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>{m.home_team} {m.home_score} - {m.away_score} {m.away_team}</p>
              {m.my_prediction ? (
                <p style={{ fontSize: 12, color: colors.textMuted }}>
                  Your prediction: {m.my_prediction.home} - {m.my_prediction.away} · {m.my_prediction.points > 0 ? `+${m.my_prediction.points} pts 🎉` : '0 pts'}
                </p>
              ) : (
                <p style={{ fontSize: 12, color: colors.textMuted }}>{t('gh_no_pred')}</p>
              )}
            </div>
          ))
        )
      ) : (
        board.length === 0 ? (
          <p style={{ textAlign: 'center', color: colors.textMuted, padding: '2rem 0' }}>{t('gh_no_points')}</p>
        ) : (
          board.map((b, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: `0.5px solid ${colors.border}` }}>
              <span>{i + 1}. @{b.username}</span>
              <span style={{ fontWeight: 600 }}>{b.points} pts</span>
            </div>
          ))
        )
      )}
      <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 16, textAlign: 'center' }}>
        3 points for an exact score, 1 point for the correct outcome (win/draw/loss).
      </p>
    </div>
  );
}


// ==================== RUMMY (simplified) ====================
const CARD_SUITS = ['\u2660', '\u2665', '\u2666', '\u2663'];
const CARD_RANK_LABEL = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
function cardLabel(c) { return `${CARD_RANK_LABEL[c.rank] || c.rank}${c.suit}`; }
function buildRummyDeck() {
  const deck = [];
  for (const suit of CARD_SUITS) { for (let rank = 1; rank <= 13; rank++) deck.push({ suit, rank, id: `${suit}${rank}` }); }
  for (let i = deck.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[deck[i], deck[j]] = [deck[j], deck[i]]; }
  return deck;
}
function isValidMeld(cards) {
  if (cards.length < 3) return false;
  const sameRank = cards.every((c) => c.rank === cards[0].rank);
  if (sameRank) { const suits = new Set(cards.map((c) => c.suit)); return suits.size === cards.length; }
  const sameSuit = cards.every((c) => c.suit === cards[0].suit);
  if (sameSuit) {
    const ranks = [...cards].map((c) => c.rank).sort((a, b) => a - b);
    for (let i = 1; i < ranks.length; i++) { if (ranks[i] !== ranks[i - 1] + 1) return false; }
    return true;
  }
  return false;
}
function RummyGame() {
  const [deck, setDeck] = useState([]);
  const [hand, setHand] = useState([]);
  const [selected, setSelected] = useState([]);
  const [melds, setMelds] = useState([]);
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState('');
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);

  const start = () => {
    const d = buildRummyDeck();
    setHand(d.slice(0, 10)); setDeck(d.slice(10));
    setSelected([]); setMelds([]); setScore(0); setMsg(''); setOver(false); setRunning(true);
  };
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const meld = () => {
    const chosen = hand.filter((c) => selected.includes(c.id));
    if (!isValidMeld(chosen)) { setMsg(t('gh_rummy_invalid')); return; }
    setMelds((m) => [...m, chosen]);
    setHand((h) => h.filter((c) => !selected.includes(c.id)));
    setScore((s) => s + chosen.length * 10);
    setSelected([]); setMsg(t('gh_rummy_melded'));
  };
  const draw = () => {
    if (deck.length === 0) return;
    setHand((h) => [...h, deck[0]]); setDeck((d) => d.slice(1));
  };
  const finish = () => {
    const penalty = hand.reduce((sum, c) => sum + Math.min(c.rank, 10), 0);
    const finalScore = Math.max(0, score - penalty);
    setScore(finalScore); setRunning(false); setOver(true);
    submitScore('rummy', finalScore);
  };
  const cardStyle = (c) => ({
    display: 'inline-block', width: 42, height: 58, borderRadius: 6,
    border: `2px solid ${selected.includes(c.id) ? colors.primary : colors.border}`,
    background: '#fff', margin: 3, textAlign: 'center', lineHeight: '58px', fontSize: 16, fontWeight: 700,
    color: (c.suit === '\u2665' || c.suit === '\u2666') ? '#EF4444' : '#1f2937', cursor: 'pointer', userSelect: 'none',
  });
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{'\ud83c\udccf'} {t('gh_rummy_title')} \u00b7 {t('gh_score')}: {score}</p>
      {!running && !over && (
        <button onClick={start} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_start')}</button>
      )}
      {running && (
        <>
          <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>{t('gh_rummy_help')}</p>
          <div style={{ marginBottom: 10 }}>
            {hand.map((c) => (<span key={c.id} style={cardStyle(c)} onClick={() => toggleSelect(c.id)}>{cardLabel(c)}</span>))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <button onClick={meld} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_meld')}</button>
            <button onClick={draw} disabled={deck.length === 0} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_draw')} ({deck.length})</button>
            <button onClick={finish} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_finish')}</button>
          </div>
          {msg && <p style={{ fontSize: 12, color: colors.primary }}>{msg}</p>}
          {melds.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <p style={{ fontSize: 12, color: colors.textMuted }}>{t('gh_melds')}:</p>
              {melds.map((m, i) => (<p key={i} style={{ fontSize: 13 }}>{m.map(cardLabel).join(' ')}</p>))}
            </div>
          )}
        </>
      )}
      {over && (
        <>
          <p style={{ fontWeight: 600, color: colors.primary, marginTop: 8 }}>{t('gh_final')}: {score}</p>
          <button onClick={start} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 8 }}>{t('gh_play_again')}</button>
        </>
      )}
      <LeaderboardPanel game="rummy" />
    </div>
  );
}

// ==================== SUDOKU ====================
const SUDOKU_PUZZLE = '530070000600195000098000060800060003400803001700020006060000280000419005000080079';
const SUDOKU_SOLUTION = '534678912672195348198342567859761423426853791713924856961537284287419635345286179';
function SudokuGame() {
  const [grid, setGrid] = useState(() => SUDOKU_PUZZLE.split('').map((c) => (c === '0' ? '' : c)));
  const [given] = useState(() => SUDOKU_PUZZLE.split('').map((c) => c !== '0'));
  const [selected, setSelected] = useState(null);
  const [startTime] = useState(() => Date.now());
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState('');
  const setCell = (idx, val) => { if (given[idx] || done) return; setGrid((g) => { const n = [...g]; n[idx] = val; return n; }); };
  const check = () => {
    if (grid.some((v) => v === '')) { setMsg(t('gh_sudoku_fill')); return; }
    if (grid.join('') === SUDOKU_SOLUTION) {
      const seconds = Math.round((Date.now() - startTime) / 1000);
      const score = Math.max(50, 2000 - seconds * 5);
      setDone(true); setMsg(t('gh_sudoku_solved') + ' ' + seconds + 's!');
      submitScore('sudoku', score);
    } else { setMsg(t('gh_sudoku_wrong')); }
  };
  const reset = () => { setGrid(SUDOKU_PUZZLE.split('').map((c) => (c === '0' ? '' : c))); setSelected(null); setDone(false); setMsg(''); };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{'\ud83d\udd22'} {t('gh_sudoku_title')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 1, maxWidth: 320, margin: '0 auto', background: '#1f2937', border: '2px solid #1f2937' }}>
        {grid.map((v, i) => {
          const row = Math.floor(i / 9), col = i % 9;
          const thickRight = col === 2 || col === 5;
          const thickBottom = row === 2 || row === 5;
          return (
            <div key={i} onClick={() => !given[i] && !done && setSelected(i)}
              style={{
                aspectRatio: '1', background: given[i] ? '#F1EFE8' : (selected === i ? colors.primaryBg : '#fff'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: given[i] ? 700 : 500, color: given[i] ? colors.text : colors.primary,
                cursor: given[i] ? 'default' : 'pointer',
                borderRight: thickRight ? '2px solid #1f2937' : '0.5px solid #ddd',
                borderBottom: thickBottom ? '2px solid #1f2937' : '0.5px solid #ddd',
              }}>{v}</div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} onClick={() => selected !== null && setCell(selected, String(n))} style={{ width: 32, height: 32, fontSize: 14, fontWeight: 600, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{n}</button>
        ))}
        <button onClick={() => selected !== null && setCell(selected, '')} style={{ padding: '0 10px', height: 32, fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>\u2715</button>
      </div>
      <div style={{ marginTop: 10 }}>
        <button onClick={check} style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer', marginRight: 8 }}>{t('gh_check')}</button>
        <button onClick={reset} style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_restart')}</button>
      </div>
      {msg && <p style={{ fontSize: 13, color: done ? colors.primary : colors.danger, marginTop: 8 }}>{msg}</p>}
      <LeaderboardPanel game="sudoku" />
    </div>
  );
}


// ==================== TIC TAC TOE ====================
function TicTacToeGame() {
  const empty = Array(9).fill(null);
  const [board, setBoard] = useState(empty);
  const [winner, setWinner] = useState(null);
  const lines = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
  const checkWinner = (b) => {
    for (const [a, c, d] of lines) { if (b[a] && b[a] === b[c] && b[a] === b[d]) return b[a]; }
    return b.every(Boolean) ? 'draw' : null;
  };
  const aiMove = (b) => {
    const empties = b.map((v, i) => (v ? null : i)).filter((v) => v !== null);
    for (const i of empties) { const t = [...b]; t[i] = 'O'; if (checkWinner(t) === 'O') return i; }
    for (const i of empties) { const t = [...b]; t[i] = 'X'; if (checkWinner(t) === 'X') return i; }
    return empties[Math.floor(Math.random() * empties.length)];
  };
  const play = (i) => {
    if (board[i] || winner) return;
    const b = [...board]; b[i] = 'X';
    let w = checkWinner(b);
    if (!w) { const ai = aiMove(b); if (ai != null) b[ai] = 'O'; w = checkWinner(b); }
    setBoard(b);
    if (w) { setWinner(w); submitScore('tictactoe', w === 'X' ? 100 : w === 'draw' ? 30 : 10); }
  };
  const reset = () => { setBoard(empty); setWinner(null); };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_ttt_title')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,60px)', gap: 4, justifyContent: 'center', marginBottom: 10 }}>
        {board.map((v, i) => (
          <button key={i} onClick={() => play(i)} style={{ width: 60, height: 60, fontSize: 26, fontWeight: 700, border: `2px solid ${colors.border}`, borderRadius: 8, background: '#fff', color: v === 'X' ? colors.primary : colors.danger, cursor: 'pointer' }}>{v}</button>
        ))}
      </div>
      {winner && <p style={{ fontWeight: 600, color: winner === 'X' ? colors.primary : colors.danger, marginBottom: 8 }}>{winner === 'draw' ? t('gh_draw_result') : winner === 'X' ? t('gh_won_fight') : t('gh_lost_fight')}</p>}
      <button onClick={reset} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_restart')}</button>
      <LeaderboardPanel game="tictactoe" />
    </div>
  );
}

// ==================== ROCK PAPER SCISSORS ====================
function RPSGame() {
  const OPTS = ['\u270a', '\u270b', '\u270c\ufe0f'];
  const [you, setYou] = useState(null);
  const [ai, setAi] = useState(null);
  const [result, setResult] = useState(null);
  const [wins, setWins] = useState(0);
  const [rounds, setRounds] = useState(0);
  const play = (i) => {
    const a = Math.floor(Math.random() * 3);
    setYou(i); setAi(a);
    let r;
    if (i === a) r = 'draw'; else if ((i + 1) % 3 === a) r = 'lose'; else r = 'win';
    setResult(r); setRounds((x) => x + 1);
    if (r === 'win') setWins((w) => { const nw = w + 1; submitScore('rps', nw * 20); return nw; });
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_rps_title')} - {t('gh_score')}: {wins}/{rounds}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 14 }}>
        {OPTS.map((o, i) => (<button key={i} onClick={() => play(i)} style={{ fontSize: 34, padding: '12px 18px', border: `0.5px solid ${colors.border}`, borderRadius: 12, background: '#fff', cursor: 'pointer' }}>{o}</button>))}
      </div>
      {you != null && (<p style={{ fontSize: 15, marginBottom: 8 }}>{t('gh_you')}: {OPTS[you]} vs {t('gh_opp')}: {OPTS[ai]}</p>)}
      {result && <p style={{ fontWeight: 600, color: result === 'win' ? colors.primary : result === 'lose' ? colors.danger : colors.textMuted }}>{result === 'win' ? t('gh_won_fight') : result === 'lose' ? t('gh_lost_fight') : t('gh_draw_result')}</p>}
      <LeaderboardPanel game="rps" />
    </div>
  );
}

// ==================== SIMON SAYS ====================
const SIMON_COLORS = [{ bg: '#22C55E' }, { bg: '#EF4444' }, { bg: '#F2C879' }, { bg: '#3B82F6' }];
function SimonGame() {
  const [seq, setSeq] = useState([]);
  const [playerSeq, setPlayerSeq] = useState([]);
  const [level, setLevel] = useState(0);
  const [flash, setFlash] = useState(null);
  const [status, setStatus] = useState('idle');
  const start = () => {
    const first = [Math.floor(Math.random() * 4)];
    setSeq(first); setPlayerSeq([]); setLevel(1); setStatus('showing');
  };
  useEffect(() => {
    if (status !== 'showing') return;
    let i = 0;
    const iv = setInterval(() => {
      if (i >= seq.length) { clearInterval(iv); setStatus('playing'); setFlash(null); return; }
      setFlash(seq[i]); setTimeout(() => setFlash(null), 350); i++;
    }, 650);
    return () => clearInterval(iv);
  }, [status, seq]);
  const tap = (i) => {
    if (status !== 'playing') return;
    const np = [...playerSeq, i];
    if (seq[np.length - 1] !== i) { setStatus('over'); submitScore('simon', level * 15); return; }
    setPlayerSeq(np);
    if (np.length === seq.length) {
      const nextSeq = [...seq, Math.floor(Math.random() * 4)];
      setSeq(nextSeq); setPlayerSeq([]); setLevel((l) => l + 1); setStatus('showing');
    }
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_simon_title')} - {t('gh_score')}: {level}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,80px)', gap: 6, justifyContent: 'center', marginBottom: 14 }}>
        {SIMON_COLORS.map((c, i) => (
          <button key={i} onClick={() => tap(i)} disabled={status !== 'playing'}
            style={{ width: 80, height: 80, borderRadius: 12, border: 'none', background: c.bg, opacity: flash === i ? 1 : 0.55, cursor: status === 'playing' ? 'pointer' : 'default', transition: 'opacity 120ms' }} />
        ))}
      </div>
      {status === 'idle' && <button onClick={start} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_start')}</button>}
      {status === 'showing' && <p style={{ color: colors.textMuted }}>{t('gh_simon_watch')}</p>}
      {status === 'playing' && <p style={{ color: colors.primary }}>{t('gh_simon_repeat')}</p>}
      {status === 'over' && (
        <>
          <p style={{ fontWeight: 600, color: colors.danger, marginBottom: 8 }}>{t('gh_lost_fight')} - {t('gh_final')}: {level}</p>
          <button onClick={start} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_play_again')}</button>
        </>
      )}
      <LeaderboardPanel game="simon" />
    </div>
  );
}

// ==================== WHACK A MOLE ====================
function WhackAMoleGame() {
  const [holes, setHoles] = useState(Array(9).fill(false));
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [running, setRunning] = useState(false);
  const [everStarted, setEverStarted] = useState(false);
  const moleTimer = useRef(null);
  const gameTimer = useRef(null);
  const scoreRef = useRef(0);
  const start = () => {
    setScore(0); scoreRef.current = 0; setTimeLeft(20); setRunning(true); setEverStarted(true);
    setHoles(Array(9).fill(false));
    moleTimer.current = setInterval(() => {
      setHoles(() => { const n = Array(9).fill(false); n[Math.floor(Math.random() * 9)] = true; return n; });
    }, 700);
    gameTimer.current = setInterval(() => {
      setTimeLeft((tl) => {
        if (tl <= 1) {
          clearInterval(moleTimer.current); clearInterval(gameTimer.current);
          setRunning(false); setHoles(Array(9).fill(false));
          submitScore('whackamole', scoreRef.current * 10);
          return 0;
        }
        return tl - 1;
      });
    }, 1000);
  };
  useEffect(() => () => { clearInterval(moleTimer.current); clearInterval(gameTimer.current); }, []);
  const whack = (i) => {
    if (!running || !holes[i]) return;
    setScore((s) => { const ns = s + 1; scoreRef.current = ns; return ns; });
    setHoles((h) => { const n = [...h]; n[i] = false; return n; });
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_whack_title')} - {t('gh_score')}: {score} {running && `\u23f1 ${timeLeft}s`}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,64px)', gap: 6, justifyContent: 'center', marginBottom: 14 }}>
        {holes.map((up, i) => (
          <button key={i} onClick={() => whack(i)} style={{ width: 64, height: 64, fontSize: 30, borderRadius: '50%', border: `2px solid ${colors.border}`, background: up ? '#fff' : '#F1EFE8', cursor: 'pointer' }}>{up ? '\ud83d\udc39' : ''}</button>
        ))}
      </div>
      {!running && <button onClick={start} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{everStarted ? t('gh_play_again') : t('gh_start')}</button>}
      {!running && everStarted && <p style={{ marginTop: 8, color: colors.primary, fontWeight: 600 }}>{t('gh_final')}: {score}</p>}
      <LeaderboardPanel game="whackamole" />
    </div>
  );
}

// ==================== NUMBER GUESS ====================
function NumberGuessGame() {
  const [target, setTarget] = useState(() => 1 + Math.floor(Math.random() * 50));
  const [guess, setGuess] = useState('');
  const [hint, setHint] = useState('');
  const [tries, setTries] = useState(0);
  const [won, setWon] = useState(false);
  const submit = () => {
    const g = parseInt(guess, 10);
    if (isNaN(g)) return;
    const nt = tries + 1; setTries(nt);
    if (g === target) { setWon(true); submitScore('numguess', Math.max(10, 200 - nt * 15)); }
    else setHint(g < target ? t('gh_guess_higher') : t('gh_guess_lower'));
    setGuess('');
  };
  const reset = () => { setTarget(1 + Math.floor(Math.random() * 50)); setGuess(''); setHint(''); setTries(0); setWon(false); };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_numguess_title')}</p>
      <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10 }}>{t('gh_numguess_help')}</p>
      {!won ? (
        <>
          <input type="number" value={guess} onChange={(e) => setGuess(e.target.value)} style={{ width: 100, padding: 8, fontSize: 16, textAlign: 'center', border: `0.5px solid ${colors.border}`, borderRadius: 8, marginRight: 8 }} />
          <button onClick={submit} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_check')}</button>
          {hint && <p style={{ marginTop: 8, color: colors.textMuted }}>{hint}</p>}
        </>
      ) : (
        <>
          <p style={{ color: colors.primary, fontWeight: 600, marginBottom: 8 }}>{t('gh_numguess_won')} {target} ({tries} {t('gh_numguess_tries')})</p>
          <button onClick={reset} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_play_again')}</button>
        </>
      )}
      <LeaderboardPanel game="numguess" />
    </div>
  );
}

// ==================== WORD SCRAMBLE ====================
const SCRAMBLE_WORDS = ['APPLE', 'HAPPY', 'TIGER', 'SMILE', 'GREEN', 'PLANT', 'CHAIR', 'LIGHT', 'WATER', 'MUSIC'];
function scrambleWord(w) {
  const a = w.split('');
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]]; }
  return a.join('');
}
function WordScrambleGame() {
  const [word, setWord] = useState('');
  const [scrambled, setScrambled] = useState('');
  const [guess, setGuess] = useState('');
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState('');
  const next = () => {
    const w = SCRAMBLE_WORDS[Math.floor(Math.random() * SCRAMBLE_WORDS.length)];
    let s = scrambleWord(w);
    let tries = 0;
    while (s === w && tries < 10) { s = scrambleWord(w); tries++; }
    setWord(w); setScrambled(s); setGuess(''); setMsg('');
  };
  useEffect(() => { next(); }, []);
  const submit = () => {
    if (guess.trim().toUpperCase() === word) {
      setScore((s) => { const ns = s + 10; submitScore('wordscramble', ns); return ns; });
      setMsg(t('gh_ws_correct'));
      setTimeout(next, 900);
    } else setMsg(t('gh_ws_wrong'));
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_ws_title')} - {t('gh_score')}: {score}</p>
      <p style={{ fontSize: 28, fontWeight: 700, letterSpacing: 4, marginBottom: 14 }}>{scrambled}</p>
      <input value={guess} onChange={(e) => setGuess(e.target.value)} style={{ width: 160, padding: 8, fontSize: 16, textAlign: 'center', border: `0.5px solid ${colors.border}`, borderRadius: 8, marginRight: 8, textTransform: 'uppercase' }} />
      <button onClick={submit} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_check')}</button>
      {msg && <p style={{ marginTop: 8, color: colors.primary }}>{msg}</p>}
      <div style={{ marginTop: 10 }}>
        <button onClick={next} style={{ padding: '6px 14px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_ws_skip')}</button>
      </div>
      <LeaderboardPanel game="wordscramble" />
    </div>
  );
}

// ==================== SLIDING PUZZLE ====================
function shuffledSolvable() {
  let arr = [1, 2, 3, 4, 5, 6, 7, 8, 0];
  let blank = 8;
  for (let i = 0; i < 120; i++) {
    const r = Math.floor(blank / 3), c = blank % 3;
    const moves = [];
    if (r > 0) moves.push(blank - 3);
    if (r < 2) moves.push(blank + 3);
    if (c > 0) moves.push(blank - 1);
    if (c < 2) moves.push(blank + 1);
    const m = moves[Math.floor(Math.random() * moves.length)];
    [arr[blank], arr[m]] = [arr[m], arr[blank]];
    blank = m;
  }
  return arr;
}
function SlidingPuzzleGame() {
  const [tiles, setTiles] = useState(() => shuffledSolvable());
  const [moves, setMoves] = useState(0);
  const solved = tiles.every((v, i) => (i === 8 ? v === 0 : v === i + 1));
  const move = (i) => {
    if (solved) return;
    const blank = tiles.indexOf(0);
    const r1 = Math.floor(i / 3), c1 = i % 3, r2 = Math.floor(blank / 3), c2 = blank % 3;
    if (Math.abs(r1 - r2) + Math.abs(c1 - c2) !== 1) return;
    const n = [...tiles];[n[i], n[blank]] = [n[blank], n[i]];
    setTiles(n); setMoves((m) => m + 1);
    if (n.every((v, idx) => (idx === 8 ? v === 0 : v === idx + 1))) submitScore('slidepuzzle', Math.max(20, 500 - (moves + 1) * 8));
  };
  const reset = () => { setTiles(shuffledSolvable()); setMoves(0); };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_slide_title')} - {t('gh_slide_moves')}: {moves}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,64px)', gap: 4, justifyContent: 'center', marginBottom: 14 }}>
        {tiles.map((v, i) => (
          <button key={i} onClick={() => move(i)} style={{ width: 64, height: 64, fontSize: 22, fontWeight: 700, border: v === 0 ? '2px solid transparent' : `2px solid ${colors.border}`, borderRadius: 8, background: v === 0 ? 'transparent' : '#fff', color: colors.text, cursor: v === 0 ? 'default' : 'pointer' }}>{v || ''}</button>
        ))}
      </div>
      {solved && <p style={{ color: colors.primary, fontWeight: 600, marginBottom: 8 }}>{t('gh_slide_solved')}</p>}
      <button onClick={reset} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{solved ? t('gh_play_again') : t('gh_restart')}</button>
      <LeaderboardPanel game="slidepuzzle" />
    </div>
  );
}

// ==================== CONNECT FOUR ====================
function ConnectFourGame() {
  const ROWS = 6, COLS = 7;
  const empty = () => Array(ROWS * COLS).fill(null);
  const [board, setBoard] = useState(empty());
  const [turn, setTurn] = useState('R');
  const [winner, setWinner] = useState(null);
  const idx = (r, c) => r * COLS + c;
  const dropRow = (b, c) => { for (let r = ROWS - 1; r >= 0; r--) if (!b[idx(r, c)]) return r; return -1; };
  const checkWin = (b, player) => {
    const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      if (b[idx(r, c)] !== player) continue;
      for (const [dr, dc] of dirs) {
        let cnt = 1;
        for (let k = 1; k < 4; k++) { const nr = r + dr * k, nc = c + dc * k; if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS || b[idx(nr, nc)] !== player) break; cnt++; }
        if (cnt >= 4) return true;
      }
    }
    return false;
  };
  const drop = (c) => {
    if (winner || turn !== 'R') return;
    const r = dropRow(board, c); if (r < 0) return;
    const b = [...board]; b[idx(r, c)] = 'R'; setBoard(b);
    if (checkWin(b, 'R')) { setWinner('R'); submitScore('connect4', 100); return; }
    setTurn('Y');
    setTimeout(() => {
      const cols = [...Array(COLS).keys()].filter((cc) => dropRow(b, cc) >= 0);
      if (cols.length === 0) { setWinner('draw'); setTurn('R'); return; }
      const ac = cols[Math.floor(Math.random() * cols.length)];
      const ar = dropRow(b, ac); const b2 = [...b]; b2[idx(ar, ac)] = 'Y'; setBoard(b2);
      if (checkWin(b2, 'Y')) { setWinner('Y'); submitScore('connect4', 10); }
      else if (b2.every(Boolean)) setWinner('draw');
      setTurn('R');
    }, 500);
  };
  const reset = () => { setBoard(empty()); setTurn('R'); setWinner(null); };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_c4_title')}</p>
      <div style={{ display: 'inline-block', background: '#1d4ed8', padding: 6, borderRadius: 10, marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLS}, 30px)`, gap: 3 }}>
          {board.map((v, i) => (
            <div key={i} onClick={() => drop(i % COLS)} style={{ width: 30, height: 30, borderRadius: '50%', background: v === 'R' ? '#EF4444' : v === 'Y' ? '#F2C879' : '#fff', cursor: 'pointer' }} />
          ))}
        </div>
      </div>
      {winner && <p style={{ fontWeight: 600, color: winner === 'R' ? colors.primary : winner === 'Y' ? colors.danger : colors.textMuted, marginBottom: 8 }}>{winner === 'draw' ? t('gh_draw_result') : winner === 'R' ? t('gh_won_fight') : t('gh_lost_fight')}</p>}
      <button onClick={reset} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_restart')}</button>
      <LeaderboardPanel game="connect4" />
    </div>
  );
}

// ==================== ODD ONE OUT ====================
const OOO_EMOJIS = ['\ud83c\udf4e', '\ud83c\udf4c', '\ud83c\udf47', '\ud83c\udf49', '\ud83c\udf53', '\ud83c\udf4b'];
function OddOneOutGame() {
  const [items, setItems] = useState([]);
  const [oddIdx, setOddIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState('');
  const gridSize = 9;
  const next = () => {
    const base = OOO_EMOJIS[Math.floor(Math.random() * OOO_EMOJIS.length)];
    let odd = OOO_EMOJIS[Math.floor(Math.random() * OOO_EMOJIS.length)];
    let tries = 0;
    while (odd === base && tries < 10) { odd = OOO_EMOJIS[Math.floor(Math.random() * OOO_EMOJIS.length)]; tries++; }
    const arr = Array(gridSize).fill(base);
    const pos = Math.floor(Math.random() * gridSize);
    arr[pos] = odd;
    setItems(arr); setOddIdx(pos); setMsg('');
  };
  useEffect(() => { next(); }, []);
  const pick = (i) => {
    if (i === oddIdx) {
      const ns = score + 10; setScore(ns); submitScore('oddoneout', ns);
      setMsg(t('gh_ws_correct')); setTimeout(next, 500);
    } else setMsg(t('gh_ws_wrong'));
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_odd_title')} - {t('gh_score')}: {score}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,60px)', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
        {items.map((e, i) => (<button key={i} onClick={() => pick(i)} style={{ width: 60, height: 60, fontSize: 30, border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}>{e}</button>))}
      </div>
      {msg && <p style={{ color: colors.primary }}>{msg}</p>}
      <LeaderboardPanel game="oddoneout" />
    </div>
  );
}

// ==================== COLOR MATCH ====================
const CM_COLORS = [{ name: 'RED', hex: '#EF4444' }, { name: 'GREEN', hex: '#22C55E' }, { name: 'BLUE', hex: '#3B82F6' }, { name: 'YELLOW', hex: '#F2C879' }];
function ColorMatchGame() {
  const [word, setWord] = useState(CM_COLORS[0]);
  const [displayHex, setDisplayHex] = useState(CM_COLORS[0].hex);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [running, setRunning] = useState(false);
  const [everStarted, setEverStarted] = useState(false);
  const timerRef = useRef(null);
  const scoreRef = useRef(0);
  const next = () => {
    const w = CM_COLORS[Math.floor(Math.random() * CM_COLORS.length)];
    const d = CM_COLORS[Math.floor(Math.random() * CM_COLORS.length)];
    setWord(w); setDisplayHex(d.hex);
  };
  const start = () => {
    setScore(0); scoreRef.current = 0; setTimeLeft(20); setRunning(true); setEverStarted(true); next();
    timerRef.current = setInterval(() => {
      setTimeLeft((tl) => {
        if (tl <= 1) { clearInterval(timerRef.current); setRunning(false); submitScore('colormatch', scoreRef.current * 5); return 0; }
        return tl - 1;
      });
    }, 1000);
  };
  useEffect(() => () => clearInterval(timerRef.current), []);
  const pick = (c) => {
    if (!running) return;
    if (c.name === word.name) setScore((s) => { const ns = s + 1; scoreRef.current = ns; return ns; });
    next();
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('gh_cm_title')} - {t('gh_score')}: {score} {running && `\u23f1 ${timeLeft}s`}</p>
      {running ? (
        <>
          <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>{t('gh_cm_help')}</p>
          <p style={{ fontSize: 34, fontWeight: 800, color: displayHex, marginBottom: 16 }}>{word.name}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {CM_COLORS.map((c) => (<button key={c.name} onClick={() => pick(c)} style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 8, background: c.hex, color: '#fff', cursor: 'pointer' }}>{c.name}</button>))}
          </div>
        </>
      ) : (
        <>
          {everStarted && <p style={{ color: colors.primary, fontWeight: 600, marginBottom: 8 }}>{t('gh_final')}: {score}</p>}
          <button onClick={start} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{everStarted ? t('gh_play_again') : t('gh_start')}</button>
        </>
      )}
      <LeaderboardPanel game="colormatch" />
    </div>
  );
}


// ==================== CHESS - LIVE P2P ====================
function ChessMultiplayerGame() {
  const [phase, setPhase] = useState('lobby');
  const [S, setS] = useState(null);
  const [myColor, setMyColor] = useState('w');
  const [sel, setSel] = useState(null);
  const [selMoves, setSelMoves] = useState([]);
  const [result, setResult] = useState('');
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const myColorRef = useRef('w');
  const myUsernameRef = useRef(localStorage.getItem('username') || '');

  useEffect(() => { myColorRef.current = myColor; }, [myColor]);

  const findMatch = () => {
    setPhase('waiting');
    const poll = async () => {
      try {
        const res = await fetch(`${GAME_API_BASE}/find-match/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game: 'chess_p2p' }) });
        const data = await safeJson(res);
        if (data.matched) {
          clearInterval(pollRef.current);
          const iAmWhite = myUsernameRef.current < data.opponent;
          setMyColor(iAmWhite ? 'w' : 'b');
          setS(chInit());
          connectWs(data.room);
          setPhase('playing');
        }
      } catch (e) {}
    };
    poll();
    pollRef.current = setInterval(poll, 1500);
  };

  const cancelSearch = async () => {
    clearInterval(pollRef.current);
    await fetch(`${GAME_API_BASE}/cancel-match/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game: 'chess_p2p' }) });
    setPhase('lobby');
  };

  const applyMove = (st, m, hSide) => {
    const n = chApply(st, m);
    const caps = { w: st.caps.w.slice(), b: st.caps.b.slice() };
    if (n.cap) caps[n.cap.c === 'w' ? 'b' : 'w'].push(n.cap);
    const next = { b: n.b, turn: n.turn, cast: n.cast, ep: n.ep, hist: [...st.hist, { b: st.b, turn: st.turn }], caps, moves: [...st.moves, chNotate(st, m)], over: null };
    const ms = chLegal(next, next.turn);
    const chk = chAttacked(next.b, chKingSq(next.b, next.turn), next.turn === 'w' ? 'b' : 'w');
    if (!ms.length) {
      const iWon = next.turn !== hSide;
      next.over = chk ? (iWon ? t('gh_mate_win') : t('gh_mate_lose')) : 'Stalemate';
      if (chk) submitScore('chess_p2p', iWon ? 100 : 10);
    }
    return next;
  };

  const connectWs = (roomName) => {
    const token = getAuthToken();
    const myUsername = myUsernameRef.current;
    const ws = new WebSocket(`${WS_BASE}/ws/game/${roomName}/?token=${token}`);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.username === myUsername) return;
      if (data.type === 'chess_move') {
        setS((prev) => {
          if (!prev) return prev;
          const mv = { f: data.f, t: data.t, promo: data.promo || null };
          return applyMove(prev, mv, myColorRef.current);
        });
      }
      if (data.type === 'player_left') {
        setResult(t('gh_p2p_left'));
        setPhase('done');
      }
    };
    wsRef.current = ws;
  };

  useEffect(() => () => { clearInterval(pollRef.current); wsRef.current && wsRef.current.close(); }, []);

  const clickSq = (i) => {
    if (!S || S.over || S.turn !== myColor) return;
    const p = S.b[i];
    const mv = selMoves.find((m) => m.f === sel && m.t === i && (!m.promo || m.promo === 'q'));
    if (mv) {
      setSel(null); setSelMoves([]);
      const next = applyMove(S, mv, myColor);
      setS(next);
      if (wsRef.current && wsRef.current.readyState === 1) {
        wsRef.current.send(JSON.stringify({ type: 'chess_move', f: mv.f, t: mv.t, promo: mv.promo || null, username: myUsernameRef.current }));
      }
      return;
    }
    if (p && p.c === myColor) { setSel(i); setSelMoves(chLegal(S, myColor).filter((m) => m.f === i)); }
    else { setSel(null); setSelMoves([]); }
  };

  if (phase === 'lobby') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 14, color: colors.textMuted, marginBottom: 16 }}>{t('gh_p2p_chess_title')}</p>
        <button onClick={findMatch} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_p2p_find')}</button>
      </div>
    );
  }
  if (phase === 'waiting') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ color: colors.textMuted, marginBottom: 12 }}>{t('gh_p2p_waiting')}</p>
        <button onClick={cancelSearch} style={{ padding: '8px 18px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_p2p_cancel')}</button>
      </div>
    );
  }

  const CH_UNI = { w: { k: '\u2654', q: '\u2655', r: '\u2656', b: '\u2657', n: '\u2658', p: '\u2659' }, b: { k: '\u265A', q: '\u265B', r: '\u265C', b: '\u265D', n: '\u265E', p: '\u265F' } };

  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 8 }}>{myColor === 'w' ? t('gh_p2p_you_white') : t('gh_p2p_you_black')}</p>
      {S && !S.over && <p style={{ fontSize: 13, fontWeight: 600, color: S.turn === myColor ? colors.primary : colors.textMuted, marginBottom: 8 }}>{S.turn === myColor ? t('gh_p2p_your_turn') : t('gh_p2p_opp_turn')}</p>}
      {S && (
        <div style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(8,36px)', border: `2px solid ${colors.border}`, marginBottom: 10 }}>
          {S.b.map((p, i) => {
            const r = Math.floor(i / 8), c = i % 8;
            const dark = (r + c) % 2 === 1;
            const isSel = sel === i;
            const isMoveTarget = selMoves.some((m) => m.t === i);
            return (
              <div key={i} onClick={() => clickSq(i)} style={{ width: 36, height: 36, background: isSel ? '#F2C879' : isMoveTarget ? '#BBF7D0' : dark ? '#B58863' : '#F0D9B5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, cursor: 'pointer' }}>
                {p ? CH_UNI[p.c][p.t] : ''}
              </div>
            );
          })}
        </div>
      )}
      {S && S.over && <p style={{ fontWeight: 600, color: colors.primary, marginBottom: 8 }}>{S.over}</p>}
      {result && <p style={{ fontWeight: 600, color: colors.primary, marginBottom: 8 }}>{result}</p>}
      <LeaderboardPanel game="chess_p2p" />
    </div>
  );
}

// ==================== UNO - MULTIPLAYER ====================
function buildUnoDeckSeeded(seed) {
  const rng = mulberry32(seed);
  const deck = buildUnoDeck();
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}
function UnoMultiplayerGame() {
  const [phase, setPhase] = useState('lobby');
  const [s, setS] = useState(null);
  const [pickingColor, setPickingColor] = useState(null);
  const [msg, setMsg] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const myUsernameRef = useRef(localStorage.getItem('username') || '');

  const findMatch = () => {
    setPhase('waiting');
    const poll = async () => {
      try {
        const res = await fetch(`${GAME_API_BASE}/find-match/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game: 'uno_p2p' }) });
        const data = await safeJson(res);
        if (data.matched) {
          clearInterval(pollRef.current);
          const deck = buildUnoDeckSeeded(data.seed);
          const iGoFirst = myUsernameRef.current < data.opponent;
          const myHand = iGoFirst ? deck.slice(0, 7) : deck.slice(7, 14);
          let rest = deck.slice(14);
          let discard = [];
          while (rest.length) {
            const c = rest.shift();
            if (!isNaN(parseInt(c.value, 10)) && c.color !== 'wild') { discard = [c]; break; }
            rest.push(c);
          }
          setS({ myHand, oppCount: 7, drawPile: rest, discardPile: discard, currentColor: discard[0].color, turn: iGoFirst ? 'me' : 'opp' });
          connectWs(data.room);
          setPhase('playing');
        }
      } catch (e) {}
    };
    poll();
    pollRef.current = setInterval(poll, 1500);
  };

  const cancelSearch = async () => {
    clearInterval(pollRef.current);
    await fetch(`${GAME_API_BASE}/cancel-match/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ game: 'uno_p2p' }) });
    setPhase('lobby');
  };

  const canPlay = (card, st) => {
    const top = st.discardPile[st.discardPile.length - 1];
    if (card.color === 'wild') return true;
    return card.color === st.currentColor || card.value === top.value;
  };

  const connectWs = (roomName) => {
    const token = getAuthToken();
    const myUsername = myUsernameRef.current;
    const ws = new WebSocket(`${WS_BASE}/ws/game/${roomName}/?token=${token}`);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.username === myUsername) return;
      setS((prev) => {
        if (!prev) return prev;
        const n = { ...prev, discardPile: [...prev.discardPile], drawPile: [...prev.drawPile], myHand: [...prev.myHand] };
        if (data.type === 'uno_play') {
          n.discardPile.push(data.card);
          n.currentColor = data.chosenColor || data.card.color;
          n.oppCount = Math.max(0, n.oppCount - 1);
          if (data.forcedDrawCount > 0) {
            const drawn = n.drawPile.splice(0, data.forcedDrawCount);
            n.myHand = [...n.myHand, ...drawn];
          }
          n.turn = (data.card.value === 'skip' || data.card.value === 'reverse' || data.forcedDrawCount > 0) ? 'opp' : 'me';
          if (n.oppCount === 0) { setGameOver(true); setWon(false); submitScore('uno_p2p', 10); }
        } else if (data.type === 'uno_draw') {
          n.drawPile.splice(0, 1);
          n.oppCount += 1;
          n.turn = 'me';
        } else if (data.type === 'player_left') {
          setMsg(t('gh_p2p_left'));
        }
        return n;
      });
    };
    wsRef.current = ws;
  };

  useEffect(() => () => { clearInterval(pollRef.current); wsRef.current && wsRef.current.close(); }, []);

  const send = (obj) => { if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify({ ...obj, username: myUsernameRef.current })); };

  const playCard = (index, chosenColor) => {
    setS((prev) => {
      const n = { ...prev, myHand: [...prev.myHand], discardPile: [...prev.discardPile], drawPile: [...prev.drawPile] };
      const card = n.myHand[index];
      if (!canPlay(card, n)) { setMsg(t('gh_card_no_match')); return prev; }
      n.myHand.splice(index, 1);
      n.discardPile.push(card);
      n.currentColor = card.color === 'wild' ? (chosenColor || 'red') : card.color;
      setMsg('');
      let forced = 0;
      if (card.value === 'draw2') forced = 2;
      if (card.value === 'wild_draw4') forced = 4;
      if (forced > 0) n.drawPile.splice(0, forced);
      n.turn = (card.value === 'skip' || card.value === 'reverse' || forced > 0) ? 'me' : 'opp';
      send({ type: 'uno_play', card, chosenColor: card.color === 'wild' ? (chosenColor || 'red') : null, forcedDrawCount: forced });
      if (n.myHand.length === 0) { setGameOver(true); setWon(true); submitScore('uno_p2p', 100); }
      return n;
    });
  };

  const handleCardClick = (index) => {
    if (!s || s.turn !== 'me' || gameOver) return;
    const card = s.myHand[index];
    if (!canPlay(card, s)) { setMsg(t('gh_card_no_match')); return; }
    if (card.color === 'wild') setPickingColor(index); else playCard(index);
  };

  const drawCard = () => {
    if (!s || s.turn !== 'me' || gameOver) return;
    setS((prev) => {
      const n = { ...prev, myHand: [...prev.myHand], drawPile: [...prev.drawPile] };
      if (n.drawPile.length > 0) n.myHand.push(n.drawPile.shift());
      n.turn = 'opp';
      return n;
    });
    send({ type: 'uno_draw' });
  };

  if (phase === 'lobby') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ fontSize: 14, color: colors.textMuted, marginBottom: 16 }}>{t('gh_p2p_uno_title')}</p>
        <button onClick={findMatch} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('gh_p2p_find')}</button>
      </div>
    );
  }
  if (phase === 'waiting') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <p style={{ color: colors.textMuted, marginBottom: 12 }}>{t('gh_p2p_waiting')}</p>
        <button onClick={cancelSearch} style={{ padding: '8px 18px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_p2p_cancel')}</button>
      </div>
    );
  }

  const topCard = s && s.discardPile[s.discardPile.length - 1];
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>{t('gh_p2p_opp_cards')}: {s ? s.oppCount : 0}</p>
      {topCard && <div style={{ marginBottom: 10 }}><UnoCard card={topCard} /></div>}
      {s && <p style={{ fontSize: 13, fontWeight: 600, color: s.turn === 'me' ? colors.primary : colors.textMuted, marginBottom: 10 }}>{s.turn === 'me' ? t('gh_p2p_your_turn') : t('gh_p2p_opp_turn')}</p>}
      {msg && <p style={{ fontSize: 12, color: colors.danger, marginBottom: 8 }}>{msg}</p>}
      {gameOver && <p style={{ fontWeight: 600, color: won ? colors.primary : colors.danger, marginBottom: 10 }}>{won ? t('gh_won_uno') : t('gh_lost_uno')}</p>}
      {!gameOver && s && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
          <button onClick={drawCard} disabled={s.turn !== 'me'} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('gh_draw')}</button>
        </div>
      )}
      {s && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          {s.myHand.map((c, i) => (<UnoCard key={i} card={c} onClick={() => handleCardClick(i)} />))}
        </div>
      )}
      {pickingColor !== null && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>{t('gh_wild_color')}</p>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            {UNO_COLORS.map((c) => (
              <button key={c} onClick={() => { playCard(pickingColor, c); setPickingColor(null); }} style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: UNO_COLOR_HEX[c], cursor: 'pointer' }} />
            ))}
          </div>
        </div>
      )}
      <LeaderboardPanel game="uno_p2p" />
    </div>
  );
}

export default function GamesHub() {
  const [activeGame, setActiveGame] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [playsInfo, setPlaysInfo] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
  };

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    fetch('/api/games/play-status/', { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setPlaysInfo(data))
      .catch(() => {});
  }, []);

  const handleGameClick = async (gameKey) => {
    if (!localStorage.getItem('token')) {
      alert('Please login first.');
      return;
    }
    setCheckingEligibility(true);
    try {
      const res = await fetch('/api/games/play-eligibility/', { headers: getAuthHeaders() });
      const data = await safeJson(res);
      setPlaysInfo(data);

      if (data.allowed) {
        setActiveGame(gameKey);
        setCheckingEligibility(false);
        return;
      }

      // Free plays used up - collect a 0.1 Pi payment before allowing this play
      if (!window.Pi) {
        alert('Open this page inside Pi Browser to pay with Pi.');
        setCheckingEligibility(false);
        return;
      }
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: data.price_pi, memo: `Xpert Games - unlock ${data.batch_size || 5} more plays`, metadata: { type: 'game_play_batch' } },
        {
          onReadyForServerApproval: (paymentId) => {
            fetch('/api/games/play-payment-approve/', {
              method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ payment_id: paymentId }),
            });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch('/api/games/play-payment-complete/', {
              method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ payment_id: paymentId, tx_id: txid }),
            })
              .then((r) => r.json())
              .then((result) => {
                setPlaysInfo((prev) => ({ ...prev, credits_remaining: result.credits_remaining }));
                setActiveGame(gameKey);
                setCheckingEligibility(false);
              })
              .catch(() => { alert(t('gh_pay_fail')); setCheckingEligibility(false); });
          },
          onCancel: () => setCheckingEligibility(false),
          onError: () => { alert(t('gh_pay_err')); setCheckingEligibility(false); },
        }
      );
    } catch (e) {
      setCheckingEligibility(false);
    }
  };

  const renderGame = () => {
    switch (activeGame) {
      case 'snake': return <SnakeGame />;
      case 'tetris': return <TetrisGame />;
      case 'platformer': return <PlatformerGame />;
      case 'fighter': return <FighterGame />;
      case 'uno': return <UnoGame />;
      case 'memory': return <MemoryMatchGame />;
      case 'penalty': return <PenaltyShootoutGame />;
      case 'chess': return <ChessGame />;
      case 'rummy': return <RummyGame />;
      case 'sudoku': return <SudokuGame />;
      case 'tictactoe': return <TicTacToeGame />;
      case 'rps': return <RPSGame />;
      case 'simon': return <SimonGame />;
      case 'whackamole': return <WhackAMoleGame />;
      case 'numguess': return <NumberGuessGame />;
      case 'wordscramble': return <WordScrambleGame />;
      case 'slidepuzzle': return <SlidingPuzzleGame />;
      case 'connect4': return <ConnectFourGame />;
      case 'oddoneout': return <OddOneOutGame />;
      case 'colormatch': return <ColorMatchGame />;
      case 'chessp2p': return <ChessMultiplayerGame />;
      case 'unop2p': return <UnoMultiplayerGame />;
      default: return null;
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <GamesHelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>{t('gh_title')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('gh_tagline')}</p>
          {playsInfo && playsInfo.plays_used != null && playsInfo.plays_used < playsInfo.free_plays && (
            <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 0.5rem' }}>
              {playsInfo.free_plays - playsInfo.plays_used} free plays left
            </p>
          )}
          {playsInfo && playsInfo.credits_remaining != null && playsInfo.plays_used >= playsInfo.free_plays && (
            <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 0.5rem' }}>
              {playsInfo.credits_remaining > 0
                ? `${playsInfo.credits_remaining} paid plays left in your batch`
                : 'Free plays used up - 0.1 Pi unlocks 5 more plays'}
            </p>
          )}
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer', flexShrink: 0 }}>{t('gh_how_btn')}</button>
      </div>

      {!activeGame ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginTop: '1rem' }}>
          {GAMES_LIST.map((g) => (
            <div key={g.key} onClick={() => !checkingEligibility && handleGameClick(g.key)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.5rem 1rem', textAlign: 'center', cursor: checkingEligibility ? 'wait' : 'pointer', opacity: checkingEligibility ? 0.6 : 1 }}>
              <p style={{ fontSize: 28, margin: '0 0 8px' }}>{g.label.split(' ')[0]}</p>
              <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{g.label.split(' ').slice(1).join(' ')}</p>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <button onClick={() => setActiveGame(null)} style={{ fontSize: 13, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 12 }}>{t('gh_back_arcade')}</button>
          {renderGame()}
        </div>
      )}
    </div>
  );
}
